/**
 * PDP Customize now — matches Feature Set studio preview layout + design fonts.
 * Reads config from product metafield (via data-config).
 */
(function () {
  'use strict';

  var CANVAS_REF = 720;
  var DEFAULT_TEXT_W = 42;
  var DEFAULT_TEXT_H = 14;
  var GLITTER_GRAIN_URL = (function () {
    try {
      var host = document.querySelector('[data-hg-pdp-customizer]');
      var fromHost = host && host.getAttribute('data-glitter-grain');
      if (fromHost) return fromHost;
      if (typeof window !== 'undefined' && window.HG_GLITTER_GRAIN) return window.HG_GLITTER_GRAIN;
    } catch (e) {}
    return '';
  })();

  var GLITTER_FILLS = {
    'gold-glitter': {
      fallback: '#b8860b',
      css: 'linear-gradient(145deg,#ffd700 0%,#f0c419 18%,#e6b422 38%,#d4af37 58%,#b8860b 78%,#8b6914 100%)',
    },
    'rose-gold-glitter': {
      fallback: '#b76e79',
      css: 'linear-gradient(145deg,#e8b8a8 0%,#d4927e 30%,#c07868 55%,#b76e79 78%,#8b4a52 100%)',
    },
    'silver-glitter': {
      fallback: '#8a939c',
      css: 'linear-gradient(145deg,#e8ecef 0%,#c5ccd3 30%,#a8b0b8 55%,#8a939c 78%,#5a636c 100%)',
    },
    'champagne-glitter': {
      fallback: '#b89a58',
      css: 'linear-gradient(145deg,#e8d4a8 0%,#d0b878 30%,#b89a58 55%,#a89050 78%,#7a6430 100%)',
    },
    'copper-glitter': {
      fallback: '#b87333',
      css: 'linear-gradient(145deg,#e8a868 0%,#d89050 30%,#c87838 55%,#a05a28 78%,#6e3a12 100%)',
    },
    'black-glitter': {
      fallback: '#1a1a1a',
      css: 'linear-gradient(145deg,#4a4a4a 0%,#2e2e2e 35%,#1a1a1a 60%,#0e0e0e 100%)',
    },
    'holographic-glitter': {
      fallback: '#8b7ec8',
      css: 'linear-gradient(145deg,#e070c8 0%,#5bc4d4 25%,#8b7ec8 45%,#d4c040 65%,#4caf70 85%,#5a9fd4 100%)',
    },
    'pearl-glitter': {
      fallback: '#d4cbb8',
      css: 'linear-gradient(145deg,#f5f0e8 0%,#ebe4d8 35%,#d4cbb8 65%,#b8ad98 100%)',
    },
  };

  function findGlitterFill(id) {
    return GLITTER_FILLS[String(id || '').trim()] || null;
  }

  function zoneGlitterFill(z) {
    if (!z || z.fillType !== 'glitter') return null;
    return findGlitterFill(z.glitterId);
  }

  function glitterTextStyleCss(glitter) {
    if (!glitter) return '';
    var css = '--hg-glitter-fill:' + glitter.css + ';';
    if (GLITTER_GRAIN_URL) {
      css += '--hg-glitter-grain:url("' + String(GLITTER_GRAIN_URL).replace(/"/g, '\\"') + '");';
    }
    return css;
  }

  function zoneFillStyleCss(z) {
    var glitter = zoneGlitterFill(z);
    if (glitter) return glitterTextStyleCss(glitter);
    return z.color ? 'color:' + escapeCss(z.color) + ';' : 'color:#1b2430;';
  }

  function parseConfig(raw) {
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    try {
      return JSON.parse(raw);
    } catch (e) {
      try {
        var textarea = document.createElement('textarea');
        textarea.innerHTML = raw;
        return JSON.parse(textarea.value);
      } catch (e2) {
        return null;
      }
    }
  }

  function configHasLayers(entry) {
    if (!entry) return false;
    if (Array.isArray(entry.zones) && entry.zones.length) return true;
    return (
      Array.isArray(entry.steps) &&
      entry.steps.some(function (step) {
        return step && Array.isArray(step.zones) && step.zones.length;
      })
    );
  }

  function normalizeProductConfig(raw) {
    if (!raw) return null;
    if (raw.bySku && typeof raw.bySku === 'object' && !Array.isArray(raw.bySku)) {
      var bySkuNorm = {};
      Object.keys(raw.bySku).forEach(function (k) {
        var uk = String(k || '')
          .trim()
          .toUpperCase();
        if (!uk) return;
        var entry = raw.bySku[k];
        if (
          !bySkuNorm[uk] ||
          Number((entry && entry.updatedAt) || 0) >= Number((bySkuNorm[uk] && bySkuNorm[uk].updatedAt) || 0)
        ) {
          bySkuNorm[uk] = entry;
        }
      });
      return {
        version: raw.version || 4,
        enabled: raw.enabled !== false,
        bySku: bySkuNorm,
      };
    }
    var legacySku = String(raw.sku || '')
      .trim()
      .toUpperCase();
    if (legacySku && configHasLayers(raw)) {
      var bySku = {};
      bySku[legacySku] = raw;
      return { version: 4, enabled: true, bySku: bySku };
    }
    return null;
  }

  function configForSku(productConfig, sku) {
    if (!productConfig || !productConfig.bySku) return null;
    var key = String(sku || '').trim();
    if (!key) return null;
    if (productConfig.bySku[key]) return productConfig.bySku[key];
    var upper = key.toUpperCase();
    if (productConfig.bySku[upper]) return productConfig.bySku[upper];
    var keys = Object.keys(productConfig.bySku);
    for (var i = 0; i < keys.length; i++) {
      if (String(keys[i] || '').trim().toUpperCase() === upper) {
        return productConfig.bySku[keys[i]];
      }
    }
    return null;
  }

  function lookupVariantById(productId, variantId) {
    var map = window.__hadiyahPdpVariants || {};
    var list = map[String(productId)] || map[productId] || [];
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === String(variantId)) return list[i];
    }
    return null;
  }

  function toggleDefaultBuyButtons(root, showDefault) {
    var scope = root.closest('.buy-buttons-block') || root.closest('.product-form') || root.parentElement || document;
    var customizer = scope.querySelector('[data-hg-pdp-customizer]') || root;
    var defaultAtc = scope.querySelector('[data-hg-default-atc]');
    var defaultAccel = scope.querySelector('[data-hg-default-accelerated]');
    if (customizer) {
      customizer.hidden = !!showDefault;
      customizer.classList.toggle('is-sku-inactive', !!showDefault);
    }
    if (defaultAtc) defaultAtc.hidden = !showDefault;
    if (defaultAccel) defaultAccel.hidden = !showDefault;
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeCss(str) {
    return String(str || '').replace(/["\\]/g, '\\$&');
  }

  function cssFontFamilyValue(family) {
    var raw = String(family || '').trim();
    if (!raw) return 'Georgia, serif';
    return raw.replace(/"/g, "'");
  }

  function fieldMaxLength(z) {
    var n = Number(z && z.maxLength);
    return isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }

  function resolvePersonalizationApiUrl(raw) {
    var url = String(raw || '').trim();
    if (!url) return '';
    // Old Vercel project now 307-redirects; browsers treat that as Failed to fetch.
    return url.replace(
      /https:\/\/hg-customization-studio\.vercel\.app/gi,
      'https://hg-customization-studio-lime.vercel.app'
    );
  }

  function shopifyRoutesRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return String(window.Shopify.routes.root);
    }
    return '/';
  }

  function toSameOriginPath(url) {
    var raw = String(url || '').trim();
    if (!raw) return '';
    if (raw.indexOf('http://') === 0 || raw.indexOf('https://') === 0) {
      try {
        return new URL(raw).pathname + new URL(raw).search;
      } catch (e) {
        return '';
      }
    }
    return raw.charAt(0) === '/' ? raw : '/' + raw;
  }

  function cartAddUrl() {
    var fromRoutes = window.routes && window.routes.cart_add_url;
    var path = toSameOriginPath(fromRoutes) || shopifyRoutesRoot().replace(/\/?$/, '/') + 'cart/add';
    // Match product-form.js: post to cart_add_url (usually /cart/add), not a forced .js URL.
    return path.replace(/\.js$/i, '') || '/cart/add';
  }

  function cartJsonUrl() {
    var fromRoutes = window.routes && window.routes.cart_url;
    var path = toSameOriginPath(fromRoutes) || shopifyRoutesRoot().replace(/\/?$/, '/') + 'cart';
    return /\.js(\?|$)/.test(path) ? path : path.replace(/\/?$/, '') + '.js';
  }

  function cartPageUrl() {
    var fromRoutes = window.routes && window.routes.cart_url;
    return toSameOriginPath(fromRoutes) || shopifyRoutesRoot().replace(/\/?$/, '/') + 'cart';
  }

  function sanitizeCartProperties(properties) {
    var clean = {};
    Object.keys(properties || {}).forEach(function (key) {
      var value = properties[key];
      if (value == null || value === '') return;
      var str = String(value);
      if (/^data:/i.test(str)) return;
      if (str.length > 4500) str = str.slice(0, 4500);
      clean[key] = str;
    });
    return clean;
  }

  function isAmbiguousCartNetworkError(err) {
    if (!err) return false;
    if (err.name === 'AbortError') return true;
    if (typeof TypeError !== 'undefined' && err instanceof TypeError) return true;
    return /failed to fetch|networkerror|load failed|network request failed/i.test(err.message || '');
  }

  function networkErrorMessage(err, fallback) {
    if (isAmbiguousCartNetworkError(err)) {
      return 'We could not confirm the cart update. Please check your cart before trying again.';
    }
    return (err && err.message) || fallback || 'Something went wrong';
  }

  function cartLineMatchesProperties(item, properties) {
    if (!item) return false;
    var itemProps = item.properties || {};
    return Object.keys(properties || {}).every(function (key) {
      return String(itemProps[key] == null ? '' : itemProps[key]) === String(properties[key]);
    });
  }

  function countMatchingCartLines(cart, variantId, properties) {
    if (!cart || !cart.items) return 0;
    var vid = String(variantId);
    return cart.items.reduce(function (total, item) {
      var id = String(item.variant_id || item.id || '');
      if (id !== vid) return total;
      if (!cartLineMatchesProperties(item, properties)) return total;
      return total + (Number(item.quantity) || 0);
    }, 0);
  }

  function fetchCartJson() {
    return fetch(cartJsonUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (res) {
      if (!res.ok) throw new Error('Could not read cart');
      return res.json();
    });
  }

  function reconcileAmbiguousCartAdd(variantId, quantity, properties, baselineQty) {
    if (baselineQty == null) return Promise.resolve(null);
    return fetchCartJson()
      .then(function (cart) {
        var current = countMatchingCartLines(cart, variantId, properties);
        var requested = Math.max(1, Number(quantity) || 1);
        if (current >= baselineQty + requested) return cart;
        return null;
      })
      .catch(function () {
        return null;
      });
  }

  function getCartAddSectionsParam() {
    var cart = document.querySelector('cart-drawer');
    if (cart && typeof cart.getSectionsToRender === 'function') {
      return cart
        .getSectionsToRender()
        .map(function (section) {
          return section.id;
        })
        .join(',');
    }

    var parts = ['cart-drawer', 'cart-icon-bubble'];
    var el =
      document.querySelector('cart-drawer-items') ||
      document.querySelector('cart-items-component');
    var sectionId = el && el.dataset && el.dataset.sectionId;
    if (sectionId) parts.push(sectionId);
    return parts.join(',');
  }

  function ensureCartSections(response) {
    if (response && response.sections) return Promise.resolve(response);
    var cart = document.querySelector('cart-drawer');
    if (!cart || typeof cart.getSectionsToRender !== 'function') {
      return Promise.resolve(response || {});
    }

    var sectionIds = cart
      .getSectionsToRender()
      .map(function (section) {
        return section.id;
      })
      .join(',');

    return fetch(window.location.pathname + '?sections=' + encodeURIComponent(sectionIds), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (sections) {
        return Object.assign({}, response || {}, { sections: sections });
      })
      .catch(function () {
        return response || {};
      });
  }

  function findProductForm(root) {
    return (
      root.closest('form[data-type="add-to-cart-form"]') ||
      root.closest('product-form-component')?.querySelector('form') ||
      document.querySelector('product-info form[data-type="add-to-cart-form"]') ||
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  function getVariantId(root, form) {
    var input =
      (form && form.querySelector('[name="id"]')) ||
      document.querySelector('product-info [name="id"]');
    if (input && input.value) return String(input.value);
    return root.getAttribute('data-variant-id') || '';
  }

  function getVariantSku(root, form, variantId, productConfig) {
    var pid = root.getAttribute('data-product-id') || '';
    var variant = lookupVariantById(pid, variantId);
    if (variant && variant.sku) return String(variant.sku).trim();

    var rootVid = root.getAttribute('data-variant-id') || '';
    if (rootVid && String(rootVid) === String(variantId)) {
      var attrSku = root.getAttribute('data-variant-sku') || '';
      if (attrSku) return String(attrSku).trim();
    }

    if (productConfig && productConfig.sku) return String(productConfig.sku);

    var select =
      (form && form.querySelector('[name="id"]')) || document.querySelector('product-info [name="id"]');
    if (select && select.tagName === 'SELECT') {
      var opt = select.options[select.selectedIndex];
      if (opt && opt.dataset && opt.dataset.sku) return String(opt.dataset.sku).trim();
    }

    if (window.Shopify && Array.isArray(window.Shopify.variants)) {
      var match = window.Shopify.variants.find(function (v) {
        return String(v.id) === String(variantId);
      });
      if (match && match.sku) return String(match.sku).trim();
    }

    return '';
  }

  function generateCustomizationId() {
    if (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function') {
      return globalThis.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function isWebpPreviewUrl(url) {
    var value = String(url || '');
    return (
      /\.webp(\?|$)/i.test(value) ||
      /\/api\/preview-image\?/i.test(value) ||
      /blob\.vercel-storage\.com/i.test(value) ||
      /custom-previews\//i.test(value)
    );
  }

  function publishCustomizationSaved(payload) {
    try {
      globalThis.postMessage(
        {
          type: 'MYAPP_CUSTOMIZATION_SAVED',
          payload: payload,
        },
        globalThis.location.origin
      );
    } catch (e) {
      /* ignore */
    }
    document.dispatchEvent(
      new CustomEvent('hg:customization-saved', {
        detail: payload,
      })
    );
  }

  function updatePdpSavedPreview(rootEl, previewUrl) {
    if (!previewUrl || !isWebpPreviewUrl(previewUrl)) return;

    var productRoot =
      rootEl.closest('.product-information') ||
      rootEl.closest('product-info') ||
      rootEl.closest('.product') ||
      document.querySelector('.product-information') ||
      document.body;

    // Prefer injecting into the PDP media gallery so it matches other product images.
    var gallery =
      (productRoot && productRoot.querySelector('media-gallery')) ||
      document.querySelector('media-gallery');
    if (gallery && injectPreviewIntoMediaGallery(gallery, previewUrl)) {
      // Remove legacy under-button preview if present.
      var legacy = productRoot.querySelector('[data-hg-pdp-saved-preview]');
      if (legacy) legacy.remove();
      return;
    }

    var host = rootEl.closest('.product') || rootEl.parentElement;
    if (!host) return;
    var slot = host.querySelector('[data-hg-pdp-saved-preview]');
    if (!slot) {
      slot = document.createElement('div');
      slot.className = 'hg-pdp-saved-preview';
      slot.setAttribute('data-hg-pdp-saved-preview', '');
      slot.innerHTML =
        '<p class="hg-pdp-saved-preview__label">Your customization</p>' +
        '<img class="hg-pdp-saved-preview__image" alt="Your customization" loading="lazy" decoding="async">';
      rootEl.insertAdjacentElement('afterend', slot);
    }
    var img = slot.querySelector('img');
    if (img) img.src = previewUrl;
    slot.hidden = false;
  }

  function injectPreviewIntoMediaGallery(gallery, previewUrl) {
    if (!gallery || !previewUrl) return false;

    var mainWrapper =
      gallery.querySelector('.swiper-main .swiper-wrapper') ||
      gallery.querySelector('.swiper__slides') ||
      gallery.querySelector('.media-gallery__grid');
    if (!mainWrapper) return false;

    var mediaId = 'hg-custom-preview';
    var existingSlide = gallery.querySelector('[data-hg-custom-preview-slide]');
    var existingThumb = gallery.querySelector('[data-hg-custom-preview-thumb]');

    var sampleSlide =
      mainWrapper.querySelector('.swiper-slide.product-media-container--image') ||
      mainWrapper.querySelector('.swiper-slide.product-media-container') ||
      mainWrapper.querySelector('.media-gallery__grid-item') ||
      mainWrapper.querySelector('.swiper-slide');
    if (!sampleSlide) return false;

    var sampleImg = sampleSlide.querySelector('img');
    var aspect =
      (sampleSlide.style && sampleSlide.style.getPropertyValue('--media-preview-ratio')) ||
      (sampleSlide.style && sampleSlide.style.getPropertyValue('--aspect-ratio')) ||
      '';

    function fillSlideImages(slideEl) {
      slideEl.querySelectorAll('img').forEach(function (img) {
        img.src = previewUrl;
        img.srcset = '';
        img.removeAttribute('srcset');
        img.sizes = img.sizes || '100vw';
        img.alt = 'Your customization';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'cover';
        img.style.display = 'block';
      });
      var mediaNode = slideEl.querySelector('[data-media-id]');
      if (mediaNode) mediaNode.setAttribute('data-media-id', mediaId);
      slideEl.setAttribute('data-hg-custom-preview-slide', '');
      slideEl.setAttribute('data-media-id', mediaId);
      if (aspect) {
        slideEl.style.setProperty('--media-preview-ratio', aspect);
        slideEl.style.setProperty('--aspect-ratio', aspect);
      }
    }

    var slide;
    if (existingSlide) {
      slide = existingSlide;
      fillSlideImages(slide);
    } else {
      slide = sampleSlide.cloneNode(true);
      fillSlideImages(slide);
      // Drop zoom / dialog hooks that point at original media.
      slide.removeAttribute('data-zoom-index');
      slide.removeAttribute('data-zoom-target');
      slide.querySelectorAll('[data-zoom-index],[data-zoom-target]').forEach(function (n) {
        n.removeAttribute('data-zoom-index');
        n.removeAttribute('data-zoom-target');
      });
      mainWrapper.appendChild(slide);
    }

    var thumbWrapper =
      gallery.querySelector('.swiper-controls__thumbnails .swiper-wrapper') ||
      gallery.querySelector('.swiper-controls__thumbnails-container .swiper-wrapper');
    if (thumbWrapper) {
      var sampleThumb =
        thumbWrapper.querySelector('.swiper-controls__thumbnail') ||
        thumbWrapper.querySelector('.swiper-slide');
      var thumb;
      if (existingThumb) {
        thumb = existingThumb;
      } else if (sampleThumb) {
        thumb = sampleThumb.cloneNode(true);
        thumb.setAttribute('data-hg-custom-preview-thumb', '');
        thumbWrapper.appendChild(thumb);
      }
      if (thumb) {
        var thumbIndex = Array.prototype.indexOf.call(thumbWrapper.children, thumb);
        thumb.setAttribute('data-index', String(thumbIndex >= 0 ? thumbIndex : thumbWrapper.children.length - 1));
        thumb.setAttribute('aria-label', 'Your customization');
        if (aspect) thumb.style.setProperty('--aspect-ratio', aspect);
        thumb.querySelectorAll('img').forEach(function (img) {
          img.src = previewUrl;
          img.srcset = '';
          img.removeAttribute('srcset');
          img.alt = 'Your customization';
          img.style.width = '100%';
          img.style.height = '100%';
          img.style.objectFit = 'cover';
        });
        thumb.addEventListener(
          'click',
          function (e) {
            e.preventDefault();
            if (typeof gallery.setActiveMedia === 'function') {
              gallery.setActiveMedia(mediaId, true);
            }
          },
          { once: false }
        );
      }
    }

    // Refresh swiper if present so the new slide is recognized.
    try {
      gallery.querySelectorAll('swiper-component').forEach(function (comp) {
        if (comp.initSwiper && typeof comp.initSwiper.update === 'function') {
          comp.initSwiper.update();
        }
      });
    } catch (e) {
      /* ignore */
    }

    if (typeof gallery.setActiveMedia === 'function') {
      setTimeout(function () {
        gallery.setActiveMedia(mediaId, true);
      }, 60);
    } else {
      try {
        var swiperComp = gallery.querySelector('.swiper-main swiper-component') || gallery.querySelector('swiper-component');
        if (swiperComp && swiperComp.initSwiper) {
          var slides = swiperComp.querySelectorAll('.swiper-wrapper > .swiper-slide');
          var idx = Array.prototype.indexOf.call(slides, slide);
          if (idx >= 0) swiperComp.initSwiper.slideTo(idx, 0);
        }
      } catch (e2) {
        /* ignore */
      }
    }

    return true;
  }

  function getQuantity(form) {
    var formData = form ? new FormData(form) : null;
    var n = formData ? parseInt(formData.get('quantity'), 10) : 1;
    return n > 0 ? n : 1;
  }

  function collectFormProperties(form) {
    var properties = {};
    if (!form) return properties;

    var formData = new FormData(form);
    formData.forEach(function (value, key) {
      if (!key.startsWith('properties[') || value == null || value === '') return;
      var match = key.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = String(value);
    });

    form.querySelectorAll('[name^="properties["]').forEach(function (input) {
      if (input.disabled || input.type === 'file') return;
      if (input.value == null || input.value === '') return;
      var match = input.name.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = input.value;
    });

    return properties;
  }

  function validateDelivery(form) {
    var picker = form && form.querySelector('hadiyah-delivery-picker');
    if (!picker) return { ok: true };
    if (typeof picker.isComplete === 'function' && !picker.isComplete()) {
      return {
        ok: false,
        message: 'Please choose an available delivery date (and time, if required) before adding to bag.',
      };
    }
    return { ok: true };
  }

  function openCartUi(response) {
    var cart = document.querySelector('cart-drawer');
    if (cart && cart.classList.contains('is-empty')) {
      cart.classList.remove('is-empty');
    }

    var openAddon = function (addon) {
      var opener = addon && (addon.openWithCartResponse || (window.CartAddonDrawer && window.CartAddonDrawer.openWithCartResponse));
      if (opener) {
        return opener.call(window.CartAddonDrawer || addon, response);
      }
      if (cart && typeof cart.renderContents === 'function') {
        if (typeof cart.renderCartJson === 'function') return cart.renderCartJson(response);
        return ensureCartSections(response).then(function (full) {
          return cart.renderContents(full);
        });
      }
      window.location.href = cartPageUrl();
    };

    return Promise.resolve().then(function () {
      return window.__ensureCartAddonDrawer ? window.__ensureCartAddonDrawer() : window.CartAddonDrawer;
    }).then(openAddon).catch(function () {
      // The purchase is already confirmed. Recover the UI without another add.
      if (cart && cart.endCartHandoff) cart.endCartHandoff();
      return Promise.resolve().then(function () { return openAddon(null); }).catch(function () {
        window.location.href = cartPageUrl();
      });
    });
  }

  function formatDateDisplay(iso, format, datePart) {
    if (!iso) return '';
    var parts = String(iso).split('-');
    if (parts.length !== 3) return iso;
    var y = Number(parts[0]);
    var m = Number(parts[1]) - 1;
    var d = Number(parts[2]);
    var date = new Date(y, m, d);
    if (isNaN(date.getTime())) return iso;
    var monthsLong = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    var monthsShort = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ];
    if (datePart === 'year') return String(y);
    if (datePart === 'day') return String(d);
    if (datePart === 'month') {
      if (format === 'medium' || format === 'monthYearShort') return monthsShort[m] || '';
      return monthsLong[m] || '';
    }
    var mm = String(m + 1);
    var dd = String(d);
    if (mm.length < 2) mm = '0' + mm;
    if (dd.length < 2) dd = '0' + dd;
    if (format === 'short') return mm + '/' + dd + '/' + y;
    if (format === 'iso') return y + '-' + mm + '-' + dd;
    if (format === 'medium') return monthsShort[m] + ' ' + d + ', ' + y;
    if (format === 'dayFirst') return d + ' ' + monthsLong[m] + ' ' + y;
    if (format === 'monthYear') return monthsLong[m] + '\u00A0' + y;
    if (format === 'monthYearShort') return monthsShort[m] + '\u00A0' + y;
    return monthsLong[m] + ' ' + d + ', ' + y;
  }

  function looksLikeNumericDate(value) {
    var s = String(value || '').trim();
    return (
      /^\d{4}-\d{2}-\d{2}$/.test(s) ||
      /^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4}$/.test(s) ||
      /^\d{1,2}$/.test(s)
    );
  }

  function effectiveTextFontSize(z) {
    return Number(z.fontSize) || 39;
  }

  var fitMeasureCtx = null;
  var fitFontRevision = 0;

  function fitZoneText(zoneEl) {
    if (!zoneEl) return;
    var textEl = zoneEl.querySelector('.hg-pdp-modal__zone-text');
    if (!textEl) return;
    if (zoneEl.classList.contains('is-placeholder') || !zoneEl.classList.contains('is-fixed-size')) {
      textEl.style.fontSize = '';
      textEl.style.whiteSpace = '';
      zoneEl._hgFitKey = null;
      return;
    }
    var text = (textEl.textContent || '').trim();
    if (!text) {
      textEl.style.fontSize = '';
      zoneEl._hgFitKey = null;
      return;
    }
    var computed = window.getComputedStyle(textEl);
    var maxFont =
      parseFloat(zoneEl.style.fontSize) ||
      parseFloat(computed.fontSize) ||
      39;
    // Match studio: shrink as far as needed so dates/names don't clip mid-glyph.
    var minFont = 6;
    var maxW = Math.max(0, zoneEl.clientWidth);
    var maxH = Math.max(0, zoneEl.clientHeight);
    if (maxW <= 2 || maxH <= 2) return;

    // Cache inputs, never the fitted output. Font readiness changes metrics even
    // when the computed family and zone dimensions are unchanged.
    var fitKey = JSON.stringify([text, maxFont, maxW, maxH, computed.fontFamily,
      computed.fontWeight, computed.fontStyle, computed.letterSpacing, fitFontRevision]);
    if (zoneEl._hgFitKey === fitKey) return;
    zoneEl._hgFitKey = fitKey;

    textEl.style.lineHeight = '1.15';
    textEl.style.maxWidth = '100%';
    textEl.style.width = '100%';
    textEl.style.display = 'block';
    textEl.style.overflow = 'hidden';

    function overflows() {
      return textEl.scrollWidth > maxW + 1 || textEl.scrollHeight > maxH + 0.5;
    }

    textEl.style.whiteSpace = 'nowrap';
    textEl.style.overflowWrap = 'normal';
    textEl.style.wordBreak = 'normal';
    // Search the same half-pixel grid and retain the original 360-step bound.
    // Overflow is monotone on this grid; measure O(log n) candidates instead of
    // forcing a layout for every half-pixel decrement.
    function fittedSize(tooLarge) {
      var low = 0;
      var high = Math.min(360, Math.max(0, Math.ceil((maxFont - minFont) / 0.5)));
      textEl.style.fontSize = maxFont + 'px';
      if (!tooLarge()) return maxFont;
      while (low < high) {
        var middle = Math.floor((low + high) / 2);
        textEl.style.fontSize = (maxFont - middle * 0.5) + 'px';
        if (tooLarge()) low = middle + 1;
        else high = middle;
      }
      return maxFont - low * 0.5;
    }
    textEl.style.fontSize = fittedSize(overflows) + 'px';

    if (overflows()) {
      textEl.style.whiteSpace = 'normal';
      textEl.style.overflowWrap = 'normal';
      textEl.style.wordBreak = 'normal';
      textEl.style.fontSize = fittedSize(function () {
        return textEl.scrollHeight > maxH + 0.5;
      }) + 'px';
    }
  }

  function fitAllZoneText(root) {
    if (!root) return;
    root.querySelectorAll('.hg-pdp-modal__zone.is-fixed-size').forEach(fitZoneText);
  }

  function openDatePicker(input) {
    if (!input || input.type !== 'date' || typeof input.showPicker !== 'function') return;
    try {
      input.showPicker();
    } catch (err) {
      /* Some browsers block showPicker without a direct user gesture */
    }
  }

  function setFontStylesheetSource(link, href) {
    link._hgReady = new Promise(function (resolve) {
      function finish(loaded) {
        clearTimeout(timer);
        link.removeEventListener('load', onLoad);
        link.removeEventListener('error', onError);
        resolve(loaded);
      }
      function onLoad() { finish(true); }
      function onError() { finish(false); }
      var timer = setTimeout(onError, 8000);
      link.addEventListener('load', onLoad);
      link.addEventListener('error', onError);
      link.href = href;
    });
    return link._hgReady;
  }

  function ensureFonts(zones) {
    var families = [];
    var faceCss = [];
    (zones || []).forEach(function (z) {
      var fam = String(z.fontFamily || '').trim();
      if (fam && !z.fontUrl && fam.indexOf('system') === -1) {
        var key = fam.replace(/['"]/g, '').split(',')[0].trim();
        var localFamily = /^(system-ui|sans-serif|serif|monospace|cursive|fantasy|Arial|Verdana|Helvetica|Times New Roman|Georgia|Courier New|Tahoma|Trebuchet MS)$/i.test(key);
        if (key && !localFamily && families.indexOf(key) === -1) families.push(key);
      }
      if (z.fontUrl && fam) {
        var familyName = fam.replace(/^["']|["']$/g, '').split(',')[0].trim();
        var format = /woff2/i.test(z.fontUrl)
          ? 'woff2'
          : /woff/i.test(z.fontUrl)
            ? 'woff'
            : /otf/i.test(z.fontUrl)
              ? 'opentype'
              : 'truetype';
        faceCss.push(
          "@font-face{font-family:'" +
            familyName.replace(/'/g, '') +
            "';src:url(\"" +
            String(z.fontUrl).replace(/"/g, '\\"') +
            '") format("' +
            format +
            '");font-display:swap;}'
        );
      }
      if (z.googleFont && families.indexOf(String(z.googleFont).split(':')[0].replace(/\+/g, ' ')) === -1) {
        /* googleFont is already covered via family list when possible */
      }
    });
    if (faceCss.length) {
      var styleId = 'hg-pdp-uploaded-fonts';
      var style = document.getElementById(styleId);
      if (!style) {
        style = document.createElement('style');
        style.id = styleId;
        document.head.appendChild(style);
      }
      var fontCss = faceCss.join('\n');
      if (style.textContent !== fontCss) style.textContent = fontCss;
    }
    if (!families.length) return Promise.resolve(true);
    var id = 'hg-pdp-fonts';
    var existing = document.getElementById(id);
    var href =
      'https://fonts.googleapis.com/css2?family=' +
      families
        .map(function (f) {
          return encodeURIComponent(f).replace(/%20/g, '+') + ':wght@400;600;700';
        })
        .join('&family=') +
      '&display=swap';
    if (existing) {
      if (existing.href !== href) return setFontStylesheetSource(existing, href);
      return existing._hgReady || Promise.resolve(true);
    }
    var pre = document.createElement('link');
    pre.rel = 'preconnect';
    pre.href = 'https://fonts.gstatic.com';
    pre.crossOrigin = 'anonymous';
    document.head.appendChild(pre);
    var link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    var ready = setFontStylesheetSource(link, href);
    document.head.appendChild(link);
    return ready;
  }

  function isHttpImageUrl(url) {
    return !!url && /^https?:\/\//i.test(String(url).trim());
  }

  function isDataImageUrl(url) {
    return /^data:image\//i.test(String(url || '').trim());
  }

  function findProductPageImageUrl(rootEl) {
    var scope = rootEl.closest('[data-section-type="product"], .product, main') || document;
    var img = scope.querySelector(
      '.product-media img, [data-product-media] img, .product__media img, .product-gallery img, img[src*="cdn.shopify"]'
    );
    if (!img) return '';
    return String(img.currentSrc || img.src || '').trim();
  }

  function resolveBackgroundImageUrl(rootEl, productConfig) {
    var candidates = [
      (productConfig && productConfig.image) || '',
      rootEl.getAttribute('data-product-image') || '',
      findProductPageImageUrl(rootEl),
    ];
    var i;
    for (i = 0; i < candidates.length; i++) {
      var httpUrl = normalizeStoredImageUrl(candidates[i]);
      if (httpUrl) return httpUrl;
    }
    for (i = 0; i < candidates.length; i++) {
      var raw = String(candidates[i] || '').trim();
      if (isDataImageUrl(raw)) return raw;
    }
    return '';
  }

  function buildApiPreviewUrl(rootEl, customizationId) {
    var api = resolvePersonalizationApiUrl(
      rootEl.getAttribute('data-personalization-api') || window.__hgPersonalizationApi || ''
    );
    if (!api || !customizationId) return '';
    var base = api.replace(/\/api\/?$/, '');
    return base + '/api/preview-image?id=' + encodeURIComponent(customizationId);
  }

  function normalizeStoredImageUrl(url) {
    if (!url) return '';
    var s = String(url).trim();
    if (isDataImageUrl(s)) return '';
    if (isHttpImageUrl(s)) return s;
    if (s.indexOf('//') === 0) return 'https:' + s;
    if (s.indexOf('/') === 0) return window.location.origin + s;
    if (/^(cdn\.shopify\.com|.*\.myshopify\.com)/i.test(s)) return 'https://' + s.replace(/^\/+/, '');
    return s;
  }

  function ensureModal() {
    var existing = document.getElementById('HadiyahPdpCustomizeModal');
    if (existing) {
      if (
        existing.getAttribute('data-hg-layout') !== 'split-v8' ||
        existing.getAttribute('data-hg-steps') !== '2'
      ) {
        existing.remove();
      } else {
        return existing;
      }
    }

    var oldCritical = document.getElementById('hg-pdp-modal-critical');
    if (oldCritical) oldCritical.remove();
    var critical = document.createElement('style');
    critical.id = 'hg-pdp-modal-critical';
    critical.textContent =
      '.hg-pdp-modal[data-hg-layout="split-v8"]{position:fixed!important;inset:0!important;z-index:2147483000!important;background:#eef1f4!important}' +
      '.hg-pdp-modal[data-hg-layout="split-v8"] .hg-pdp-modal__panel{display:grid!important;height:100%!important;width:100%!important;inset:0!important}' +
      '@media (min-width:860px){.hg-pdp-modal[data-hg-layout="split-v8"] .hg-pdp-modal__panel{grid-template-columns:minmax(0,62%) minmax(340px,38%)!important;grid-template-rows:1fr!important}}' +
      '.hg-pdp-modal__preview{display:flex!important;flex-direction:column!important;align-items:center!important;justify-content:center!important}' +
      '.hg-pdp-modal__canvas-wrap{position:relative!important;width:min(88vw,340px,52vh,calc(100% - 36px))!important;aspect-ratio:1!important;height:auto!important;border:1px solid #c8ccd4!important;border-radius:6px!important;background:#fff!important;box-shadow:0 8px 28px rgba(15,23,42,.1)!important;overflow:hidden!important;box-sizing:border-box!important}' +
      '@media (min-width:860px){.hg-pdp-modal__canvas-wrap{width:min(82%,520px,78vh,calc(100% - 48px))!important}}' +
      '@media (min-width:1200px){.hg-pdp-modal__canvas-wrap{width:min(78%,620px,78vh,calc(100% - 48px))!important}}' +
      '.hg-pdp-modal__canvas{position:relative!important;width:100%!important;height:100%!important;overflow:hidden!important}' +
      '.hg-pdp-modal__zones{position:absolute!important;left:0!important;top:0!important;width:720px!important;height:720px!important;transform-origin:0 0!important;opacity:0!important;pointer-events:none!important}' +
      '.hg-pdp-modal__zones.is-scaled{opacity:1!important}' +
      '.hg-pdp-modal__image-layer{position:absolute!important;box-sizing:border-box!important;pointer-events:none!important}' +
      '.hg-pdp-modal__image-layer img,.hg-pdp-modal__canvas img{position:absolute!important;inset:0!important;width:100%!important;height:100%!important;max-width:none!important;object-fit:contain!important}' +
      '.hg-pdp-modal__side{display:flex!important;flex-direction:column!important;background:#fff!important;border-left:1px solid #d9dce3!important}' +
      '.hg-pdp-modal__control{display:block!important;border:1px solid #c8ccd4!important;border-radius:8px!important;background:#fff!important;box-shadow:inset 0 0 0 1px #c8ccd4!important}' +
      '.hg-pdp-modal input.hg-pdp-modal__text-input{display:block!important;width:100%!important;height:66px!important;min-height:66px!important;border:0!important;border-radius:8px!important;background:#fff!important;padding:0 18px!important;box-shadow:none!important}' +
      '.hg-pdp-modal__date{display:flex!important;width:100%!important;height:66px!important;border:0!important;border-radius:8px!important;background:#fff!important}' +
      '.hg-pdp-modal__footer{position:relative!important;display:block!important;visibility:visible!important}' +
      '.hg-pdp-modal__add,.hg-pdp-modal button.hg-pdp-modal__add{display:block!important;visibility:visible!important;width:100%!important;background:#a68000!important;color:#fff!important}' +
      '.hg-pdp-modal__zone.is-glitter-fill .hg-pdp-modal__zone-text{background-image:' +
        (GLITTER_GRAIN_URL
          ? 'url("' + String(GLITTER_GRAIN_URL).replace(/"/g, '\\"') + '")'
          : 'url("glitter-grain.svg")') +
        ',var(--hg-glitter-fill,linear-gradient(145deg,#ffd700,#8b6914))!important;background-blend-mode:normal,normal!important;background-size:16px 16px,100% 100%!important;background-repeat:repeat,no-repeat!important;-webkit-background-clip:text!important;background-clip:text!important;color:#b8860b!important;-webkit-text-fill-color:transparent!important;text-shadow:none!important}';
    document.head.appendChild(critical);

    var modal = document.createElement('div');
    modal.id = 'HadiyahPdpCustomizeModal';
    modal.className = 'hg-pdp-modal';
    modal.setAttribute('data-hg-pdp-customizer-modal', 'true');
    modal.setAttribute('data-hg-layout', 'split-v8');
    modal.setAttribute('data-hg-steps', '2');
    modal.hidden = true;
    modal.innerHTML =
      '<div class="hg-pdp-modal__panel" role="dialog" aria-modal="true" aria-label="Customize product">' +
      '<div class="hg-pdp-modal__preview">' +
      '<div class="hg-pdp-modal__canvas-wrap">' +
      '<div class="hg-pdp-modal__canvas">' +
      '<div class="hg-pdp-modal__zoom" data-hg-modal-zoom>' +
      '<div class="hg-pdp-modal__image-layer" data-hg-modal-image-layer>' +
      '<img data-hg-modal-image alt="" width="800" height="800">' +
      '</div>' +
      '<div class="hg-pdp-modal__zones" data-hg-modal-zones></div>' +
      '</div></div></div>' +
      '<p class="hg-pdp-modal__preview-label">preview</p>' +
      '</div>' +
      '<aside class="hg-pdp-modal__side">' +
      '<button type="button" class="hg-pdp-modal__close" data-hg-modal-close aria-label="Close">&times;</button>' +
      '<div class="hg-pdp-modal__side-body">' +
      '<div class="hg-pdp-modal__fields" data-hg-modal-fields></div>' +
      '</div>' +
      '<div class="hg-pdp-modal__footer" data-hg-modal-footer>' +
      '<p class="hg-pdp-modal__status" data-hg-modal-status role="status" aria-live="polite" hidden></p>' +
      '<div class="hg-pdp-modal__actions">' +
      '<button type="button" class="hg-pdp-modal__back" data-hg-modal-back hidden>Back</button>' +
      '<button type="button" class="hg-pdp-modal__add is-disabled" data-hg-modal-add aria-disabled="true">ADD TO CART</button>' +
      '</div>' +
      '</div>' +
      '</aside>' +
      '</div>';
    document.body.appendChild(modal);
    return modal;
  }

  function isMyohCustomizerRoot(el) {
    return !!(el && (el.closest('[data-myoh-root]') || el.closest('[data-myoh-customizer-host]')));
  }

  function finalStepCtaLabel(root) {
    return isMyohCustomizerRoot(root) ? 'Next' : 'ADD TO CART';
  }

  function normalizeConfigSteps(config, finalLabel) {
    if (!config) return [];
    var lastLabel = finalLabel || 'ADD TO CART';
    var list;
    if (Array.isArray(config.steps) && config.steps.length) {
      list = config.steps
        .map(function (s, i) {
          return {
            id: s.id || 'step-' + (i + 1),
            title: s.title || 'Step ' + (i + 1),
            image: s.image || '',
            imageLayer: s.imageLayer || config.imageLayer || {},
            zones: Array.isArray(s.zones) ? s.zones : [],
            ctaLabel: s.ctaLabel || 'Next',
          };
        })
        .filter(function (s) {
          return !!(s.image || (s.zones && s.zones.length));
        });
    } else {
      list = [
        {
          id: 'step-1',
          title: config.stepTitle || 'Step 1',
          image: config.image || '',
          imageLayer: config.imageLayer || {},
          zones: Array.isArray(config.zones) ? config.zones : [],
          ctaLabel: lastLabel,
        },
      ];
    }
    // Always realign CTA after filtering — stale "Next" on a lone step caused add-to-cart.
    // On Make Your Own Hamper, final step should say Next (returns to builder), not Add to cart.
    list.forEach(function (s, i) {
      s.ctaLabel = i >= list.length - 1 ? lastLabel : 'Next';
    });
    return list;
  }

  function createController(root) {
    if (root.dataset.hgBound === 'true') return;
    root.dataset.hgBound = 'true';

    var productConfig = normalizeProductConfig(parseConfig(root.getAttribute('data-config')));
    if (!productConfig || !productConfig.bySku || !Object.keys(productConfig.bySku).length) {
      // No usable SKU config — fall back to theme Add to Cart.
      toggleDefaultBuyButtons(root, true);
      return;
    }

    var config = null;
    var steps = [];
    var modal = ensureModal();
    var designFontReady = Promise.resolve(true);

    function ensureDesignFonts() {
      designFontReady = ensureFonts(steps.reduce(function (list, step) {
        return list.concat(step.zones || []);
      }, []));
    }

    function loadSkuConfig(skuConfig) {
      if (!skuConfig) return false;
      config = Object.assign({}, skuConfig);
      steps = normalizeConfigSteps(config, finalStepCtaLabel(root));
      if (
        !steps.length ||
        !steps.some(function (s) {
          return s.zones && s.zones.length;
        })
      ) {
        config = null;
        steps = [];
        return false;
      }
      config.steps = steps;
      config.image = steps[0].image || config.image || '';
      config.imageLayer = steps[0].imageLayer || config.imageLayer;
      config.zones = steps[0].zones || [];
      ensureDesignFonts();
      var preloadUrl = resolveBackgroundImageUrl(root, { image: steps[0].image || config.image });
      if (preloadUrl && !isDataImageUrl(preloadUrl)) {
        var preImg = new Image();
        preImg.decoding = 'async';
        preImg.src = preloadUrl;
      }
      return true;
    }

    function resetCustomizerState() {
      previewSession++;
      values = {};
      stepIndex = 0;
      personalizationId = '';
      clearPreviewCache();
      stopReadyStatus();
      setAddBusy(false);
      addButtonIdleLabel = '';
      setStatus('');
    }

    function applyVariantSelection(variantId, sku) {
      if (operation && operation.posting) return;
      operation = null;
      root.setAttribute('data-variant-id', String(variantId || ''));
      root.setAttribute('data-variant-sku', String(sku || ''));
      var skuConfig = configForSku(productConfig, sku);
      if (modal && modal.classList.contains('is-open') && typeof modal._hgClose === 'function') {
        modal._hgClose();
      }
      resetCustomizerState();
      if (!skuConfig || !loadSkuConfig(skuConfig)) {
        config = null;
        steps = [];
        toggleDefaultBuyButtons(root, true);
        return;
      }
      toggleDefaultBuyButtons(root, false);
    }

    function bindVariantListener() {
      function handleVariantUpdate(variant) {
        if (!variant) return;
        var vid = variant.id || variant.variant_id;
        var sku =
          (variant.sku && String(variant.sku).trim()) ||
          getVariantSku(root, findProductForm(root), vid, null);
        applyVariantSelection(vid, sku);
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
          var variant = event && event.data && event.data.variant;
          if (variant) handleVariantUpdate(variant);
        });
      }

      var variantEvent =
        (typeof window.MainEvents !== 'undefined' && window.MainEvents.variantUpdate) || 'variant:update';
      document.addEventListener(variantEvent, function (e) {
        var detail = (e && e.detail) || {};
        var variant = detail.variant || detail;
        if (variant && variant.id) handleVariantUpdate(variant);
      });

      var form = findProductForm(root);
      if (form) {
        var idInput = form.querySelector('[name="id"]');
        if (idInput) {
          idInput.addEventListener('change', function () {
            var vid = idInput.value;
            var sku = getVariantSku(root, form, vid, null);
            applyVariantSelection(vid, sku);
          });
        }
      }
    }

    var operation = null;
    var cartUncertain = false;
    var returnFocus = null;
    var inertBackground = [];
    var previousOverflow = '';
    var lockedControls = [];
    var values = {};
    var stepIndex = 0;
    var personalizationId = '';
    var stepPreviewCache = {};
    var previewSession = 0;
    var stepSaveTails = {};
    var readyStatusTimer = null;
    var readyStatusIndex = 0;
    var addButtonIdleLabel = '';
    var READY_STATUS_MESSAGES = [
      'Sharing the barakah…',
      'Getting your cart ready…',
      'Almost there…',
    ];
    var els = {
      image: modal.querySelector('[data-hg-modal-image]'),
      imageLayer: modal.querySelector('[data-hg-modal-image-layer]'),
      zones: modal.querySelector('[data-hg-modal-zones]'),
      zoom: modal.querySelector('[data-hg-modal-zoom]'),
      canvas: modal.querySelector('.hg-pdp-modal__canvas'),
      fields: modal.querySelector('[data-hg-modal-fields]'),
      status: modal.querySelector('[data-hg-modal-status]'),
      add: modal.querySelector('[data-hg-modal-add]'),
      close: modal.querySelector('[data-hg-modal-close]'),
      footer: modal.querySelector('[data-hg-modal-footer]'),
    };

    function activeStep() {
      return steps[stepIndex] || steps[0];
    }

    function isLastStep() {
      return stepIndex >= steps.length - 1;
    }

    if (!modal.dataset.hgListeners) {
      modal.dataset.hgListeners = 'true';
      modal.addEventListener('click', function (e) {
        if (e.target === modal && typeof modal._hgClose === 'function') modal._hgClose();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Tab' && modal.classList.contains('is-open')) {
          var items = Array.from(modal.querySelectorAll('button, input, select, textarea, a[href], [tabindex]'))
            .filter(function (el) { return !el.disabled && el.tabIndex >= 0 && el.getClientRects().length; });
          var first = items[0];
          var last = items[items.length - 1];
          if (!first) { e.preventDefault(); return; }
          if (e.shiftKey && (document.activeElement === first || !modal.contains(document.activeElement))) {
            e.preventDefault(); last.focus();
          } else if (!e.shiftKey && (document.activeElement === last || !modal.contains(document.activeElement))) {
            e.preventDefault(); first.focus();
          }
        }
        if (e.key === 'Escape' && modal.classList.contains('is-open') && typeof modal._hgClose === 'function') {
          modal._hgClose();
        }
      });
      if (els.close) {
        els.close.addEventListener('click', function () {
          if (typeof modal._hgClose === 'function') modal._hgClose();
        });
      }
      els.image.addEventListener('load', function () {
        if (modal._hgScheduleLayout) modal._hgScheduleLayout();
      });
      els.fields.addEventListener('input', function (e) {
        if (typeof modal._hgFieldInput === 'function') modal._hgFieldInput(e);
      });
      els.fields.addEventListener('change', function (e) {
        if (typeof modal._hgFieldInput === 'function') modal._hgFieldInput(e);
      });
      els.fields.addEventListener('focusin', function (e) {
        if (typeof modal._hgFieldFocus === 'function') modal._hgFieldFocus(e);
      });
      els.fields.addEventListener('focusout', function (e) {
        if (typeof modal._hgFieldBlur === 'function') modal._hgFieldBlur(e);
      });
      els.fields.addEventListener('click', function (e) {
        var dateBox = e.target.closest('.hg-pdp-modal__date');
        if (!dateBox) return;
        var dateInput = dateBox.querySelector('.hg-pdp-modal__date-input');
        if (!dateInput) return;
        if (document.activeElement !== dateInput) dateInput.focus();
        openDatePicker(dateInput);
      });
      if (els.add) {
        els.add.addEventListener('click', function () {
          if (els.add.classList.contains('is-disabled')) {
            if (typeof modal._hgShowErrors === 'function') modal._hgShowErrors();
            return;
          }
          if (typeof modal._hgPrimary === 'function') modal._hgPrimary();
          else if (typeof modal._hgAdd === 'function') modal._hgAdd();
        });
      }
      window.addEventListener('resize', function () {
        if (!modal.classList.contains('is-open')) return;
        if (modal._hgScheduleLayout) modal._hgScheduleLayout();
      });
      if (typeof ResizeObserver !== 'undefined' && els.canvas && !modal._hgCanvasResizeObs) {
        modal._hgCanvasResizeObs = new ResizeObserver(function () {
          if (!modal.classList.contains('is-open')) return;
          if (modal._hgScheduleLayout) modal._hgScheduleLayout();
        });
        modal._hgCanvasResizeObs.observe(els.canvas);
      }
      if (document.fonts) {
        var fontsChanged = function () {
          fitFontRevision++;
          if (modal._hgFontsChanged) modal._hgFontsChanged();
        };
        document.fonts.ready.then(fontsChanged);
        document.fonts.addEventListener('loadingdone', fontsChanged);
      }
    }

    function ensureNextButton() {
      var side = modal.querySelector('.hg-pdp-modal__side');
      if (!side) return;
      var footer = modal.querySelector('[data-hg-modal-footer]');
      if (!footer) {
        footer = document.createElement('div');
        footer.className = 'hg-pdp-modal__footer';
        footer.setAttribute('data-hg-modal-footer', '');
        footer.innerHTML =
          '<p class="hg-pdp-modal__status" data-hg-modal-status role="status" aria-live="polite" hidden></p>' +
          '<div class="hg-pdp-modal__actions">' +
          '<button type="button" class="hg-pdp-modal__back" data-hg-modal-back hidden>Back</button>' +
          '<button type="button" class="hg-pdp-modal__add is-disabled" data-hg-modal-add aria-disabled="true">ADD TO CART</button>' +
          '</div>';
        side.appendChild(footer);
      }
      var actions = footer.querySelector('.hg-pdp-modal__actions');
      if (!actions) {
        actions = document.createElement('div');
        actions.className = 'hg-pdp-modal__actions';
        var existingAdd = footer.querySelector('[data-hg-modal-add]');
        if (existingAdd) actions.appendChild(existingAdd);
        footer.appendChild(actions);
      }
      var backBtn = footer.querySelector('[data-hg-modal-back]');
      if (!backBtn) {
        backBtn = document.createElement('button');
        backBtn.type = 'button';
        backBtn.className = 'hg-pdp-modal__back';
        backBtn.setAttribute('data-hg-modal-back', '');
        backBtn.textContent = 'Back';
        backBtn.hidden = true;
        actions.insertBefore(backBtn, actions.firstChild);
      }
      var addBtn = footer.querySelector('[data-hg-modal-add]');
      if (!addBtn) {
        addBtn = document.createElement('button');
        addBtn.type = 'button';
        addBtn.className = 'hg-pdp-modal__add is-disabled';
        addBtn.setAttribute('data-hg-modal-add', '');
        addBtn.setAttribute('aria-disabled', 'true');
        addBtn.textContent = 'ADD TO CART';
        actions.appendChild(addBtn);
      }
      els.footer = footer;
      els.status = footer.querySelector('[data-hg-modal-status]') || els.status;
      els.back = backBtn;
      if (els.add !== addBtn) {
        els.add = addBtn;
        addBtn.addEventListener('click', function () {
          if (els.add.classList.contains('is-disabled')) {
            if (typeof modal._hgShowErrors === 'function') modal._hgShowErrors();
            return;
          }
          if (typeof modal._hgPrimary === 'function') modal._hgPrimary();
        });
      }
      if (!backBtn.dataset.hgBound) {
        backBtn.dataset.hgBound = 'true';
        backBtn.addEventListener('click', function () {
          if (typeof modal._hgBack === 'function') modal._hgBack();
        });
      }
      syncPrimaryCta();
    }

    function syncPrimaryCta() {
      if (!els.add) return;
      var step = activeStep();
      if (isLastStep()) {
        els.add.textContent = step.ctaLabel || config.ctaLabel || finalStepCtaLabel(root);
      } else {
        els.add.textContent = step.ctaLabel || 'Next';
      }
      if (els.back) {
        els.back.hidden = steps.length < 2 || stepIndex <= 0;
      }
    }

    function sortedZones() {
      return (activeStep().zones || []).slice().sort(function (a, b) {
        return (a.order || 0) - (b.order || 0);
      });
    }

    function isEditableZone(z) {
      return z.editable !== false;
    }

    function editableZones() {
      return sortedZones().filter(isEditableZone);
    }

    function overlayIdOf(z) {
      return z.overlayId || z.id;
    }

    function formFieldsFromZones(zoneList) {
      var fields = [];
      var used = {};
      var byFormId = {};
      (zoneList || [])
        .slice()
        .sort(function (a, b) {
          return (a.fieldOrder || a.order || 0) - (b.fieldOrder || b.order || 0);
        })
        .filter(isEditableZone)
        .forEach(function (z) {
          var formId = z.formId || z.id;
          if (!byFormId[formId]) byFormId[formId] = [];
          byFormId[formId].push(z);
        });

      Object.keys(byFormId).forEach(function (formId) {
        if (used[formId]) return;
        used[formId] = true;
        var group = byFormId[formId];
        var picked = group[0];
        // Prefer a full-date part / readable label over canvas tokens like "Y".
        if (group.length > 1 && isDateField(picked)) {
          picked =
            group.find(function (z) {
              return !z.datePart || z.datePart === 'full';
            }) ||
            group.find(function (z) {
              var lab = String(z.label || z.content || '').trim();
              return lab && !isDateCanvasToken(lab);
            }) ||
            picked;
        }
        fields.push(Object.assign({}, picked, { formId: formId, overlayId: overlayIdOf(picked) }));
        (picked.extraFields || []).forEach(function (extra) {
          if (!extra || !extra.id || used[extra.id]) return;
          used[extra.id] = true;
          fields.push(
            Object.assign({}, picked, extra, {
              formId: extra.id,
              overlayId: overlayIdOf(picked),
              extraFields: undefined,
              content: picked.content,
            })
          );
        });
      });
      return fields;
    }

    function formFields() {
      return formFieldsFromZones(sortedZones());
    }

    function allFormFields() {
      var fields = [];
      steps.forEach(function (step) {
        formFieldsFromZones(step.zones || []).forEach(function (f) {
          fields.push(f);
        });
      });
      return fields;
    }

    function overlayGroupsFor(zoneList) {
      var groups = [];
      var index = {};
      var sorted = (zoneList || []).slice().sort(function (a, b) {
        return (a.order || 0) - (b.order || 0);
      });
      sorted.forEach(function (z) {
        if (!isEditableZone(z)) return;
        var formId = z.formId || z.id;
        var key = overlayIdOf(z);
        var member = Object.assign({}, z, { formId: formId, overlayId: key });
        if (!index[key]) {
          index[key] = { zone: member, members: [] };
          groups.push(index[key]);
        }
        index[key].members.push(member);
        (z.extraFields || []).forEach(function (extra) {
          if (!extra) return;
          index[key].members.push(
            Object.assign({}, z, extra, {
              formId: extra.id || formId,
              overlayId: key,
              extraFields: undefined,
              content: z.content,
            })
          );
        });
      });
      sorted.forEach(function (z) {
        if (isEditableZone(z)) return;
        var key = overlayIdOf(z);
        if (index[key]) return;
        index[key] = { zone: z, members: [z] };
        groups.push(index[key]);
      });
      return groups;
    }

    function overlayGroups() {
      return overlayGroupsFor(sortedZones());
    }

    function groupIsShowingPlaceholder(group) {
      var z = group.zone;
      if (!isEditableZone(z)) return false;
      var hasValue = group.members.some(function (m) {
        var v = values[m.formId || m.id] || {};
        if (isDateField(m)) return !!v.date;
        return !!(v.text && String(v.text).trim());
      });
      if (hasValue) return false;
      return !group.members.some(function (m) {
        var v = values[m.formId || m.id] || {};
        return !!v.focused;
      });
    }

    function groupDisplayText(group) {
      var z = group.zone;
      if (!isEditableZone(z)) {
        if (isDateField(z)) {
          if (z.markDate) {
            return formatDateDisplay(z.markDate, z.dateFormat || 'long', z.datePart || 'full');
          }
          if (z.content && !looksLikeNumericDate(z.content)) return z.content;
          return z.content || z.text || '';
        }
        return z.content || z.text || '';
      }
      var parts = group.members.map(function (m) {
        var v = values[m.formId || m.id] || {};
        if (isDateField(m)) {
          return v.date
            ? formatDateDisplay(v.date, m.dateFormat || 'long', m.datePart || 'full')
            : '';
        }
        return String(v.text || '').trim();
      });
      var filled = parts.filter(Boolean);
      if (filled.length) {
        var joiner = group.members.length > 1 ? String(z.joinWith != null ? z.joinWith : ' ') : '';
        return filled.join(joiner);
      }
      var focused = group.members.some(function (m) {
        var v = values[m.formId || m.id] || {};
        return !!v.focused;
      });
      if (focused) return '';
      return zonePlaceholder(z);
    }

    function isDateField(z) {
      return z.fieldType === 'date' || z.type === 'calendar' || z.type === 'datetime';
    }

    function isGenericZoneLabel(label) {
      var s = String(label || '').trim();
      if (!s) return true;
      if (/^text\s*\d+(\s+copy|\s+\d+)?$/i.test(s)) return true;
      if (/^curve\s*text\s*\d+(\s+copy|\s+\d+)?$/i.test(s)) return true;
      if (/^element(\s+copy|\s+\d+)?$/i.test(s)) return true;
      if (/^calendar(\s+copy|\s+\d+)?$/i.test(s)) return true;
      if (/^your text here$/i.test(s)) return true;
      if (/^select date$/i.test(s)) return true;
      // Canvas tokens for day/month/year parts (e.g. "Y", "DD", "YYYY").
      if (/^(y|yy|yyyy|d|dd|m|mm|mmm|mmmm|day|month|year)$/i.test(s)) return true;
      return false;
    }

    function isGenericZoneContent(content) {
      var s = String(content || '').trim();
      if (!s) return true;
      if (/^your text here$/i.test(s)) return true;
      if (/^select date$/i.test(s)) return true;
      if (/^(y|yy|yyyy|d|dd|m|mm|mmm|mmmm|day|month|year)$/i.test(s)) return true;
      // Single-letter / tiny canvas glyphs used as design placeholders.
      if (/^[A-Za-z0-9&]$/.test(s)) return true;
      return false;
    }

    function isDateCanvasToken(value) {
      return isGenericZoneContent(value) || isGenericZoneLabel(value);
    }

    function isShortCanvasPlaceholder(content, label) {
      var s = String(content || '').trim();
      if (!s) return true;
      if (isGenericZoneContent(s)) return true;
      // "A" / "AB" / "01" on canvas while the form label is "Enter initials A".
      if (s.length <= 3 && /^[A-Za-z0-9&]+$/.test(s)) return true;
      var lab = String(label || '').trim();
      if (lab && lab.length > s.length && lab.toLowerCase().indexOf(s.toLowerCase()) !== -1) {
        return true;
      }
      return false;
    }

    function zoneDisplayName(z) {
      if (!z) return 'Text';
      var label = String(z.label || '').trim();
      var content = String(z.content || z.text || '').trim();

      // Date form labels must not use canvas placeholders like "Y" / "DD".
      if (isDateField(z)) {
        if (label && !isDateCanvasToken(label) && !/^date$/i.test(label)) {
          return label;
        }
        return 'Enter the date';
      }

      // Feature Set / form prompt wins over tiny canvas text ("A", "B", "Y").
      if (label && !isGenericZoneLabel(label)) {
        if (!content || isShortCanvasPlaceholder(content, label) || /^enter\b/i.test(label)) {
          return label;
        }
        if (label.length >= content.length) return label;
      }

      if (content && !isGenericZoneContent(content) && !isShortCanvasPlaceholder(content, label)) {
        return content;
      }
      if (label && !isGenericZoneLabel(label)) return label;
      return content || label || 'Text';
    }

    function zonePlaceholder(z) {
      if (isDateField(z)) {
        var dateContent = String(z.content || '').trim();
        if (dateContent && !isDateCanvasToken(dateContent) && !isShortCanvasPlaceholder(dateContent, z.label)) {
          return dateContent;
        }
        return 'Date';
      }
      var content = String((z && z.content) || '').trim();
      var label = String((z && z.label) || '').trim();
      if (content && !isGenericZoneContent(content) && !isShortCanvasPlaceholder(content, label)) {
        return content;
      }
      if (label && !isGenericZoneLabel(label) && !/^enter\b/i.test(label)) return label;
      if (label === '&' || label === 'and') return '&';
      if (/date|calendar/i.test(label)) return 'Date';
      if (/groom|bride|name|initial/i.test(label)) return 'Name';
      return '';
    }

    function zoneDisplayText(z, v) {
      v = v || {};
      if (!isEditableZone(z)) {
        if (isDateField(z)) {
          if (z.markDate) {
            return formatDateDisplay(z.markDate, z.dateFormat || 'long', z.datePart || 'full');
          }
          if (z.content && !looksLikeNumericDate(z.content)) return z.content;
          return z.content || z.text || '';
        }
        return z.content || z.text || '';
      }
      var focused = !!v.focused;
      if (isDateField(z)) {
        if (v.date) {
          return formatDateDisplay(v.date, z.dateFormat || 'long', z.datePart || 'full');
        }
        return focused ? '' : zonePlaceholder(z);
      }
      if (v.text && String(v.text).trim()) return String(v.text);
      return focused ? '' : zonePlaceholder(z);
    }

    function setStatus(msg, isError) {
      if (!msg) {
        els.status.hidden = true;
        els.status.textContent = '';
        els.status.classList.remove('is-readying', 'is-error');
        return;
      }
      els.status.hidden = false;
      els.status.textContent = msg;
      els.status.classList.toggle('is-error', !!isError);
      els.status.classList.toggle('is-readying', !isError);
      els.status.style.color = isError ? '#c9645b' : '';
    }

    function stopReadyStatus() {
      if (readyStatusTimer) {
        clearInterval(readyStatusTimer);
        readyStatusTimer = null;
      }
      if (els.status) els.status.classList.remove('is-readying');
    }

    function startReadyStatus() {
      stopReadyStatus();
      readyStatusIndex = 0;
      setStatus(READY_STATUS_MESSAGES[0]);
      readyStatusTimer = setInterval(function () {
        readyStatusIndex = (readyStatusIndex + 1) % READY_STATUS_MESSAGES.length;
        setStatus(READY_STATUS_MESSAGES[readyStatusIndex]);
      }, 1600);
    }

    function setAddBusy(busy, label) {
      if (!els.add) return;
      if (busy && !lockedControls.length) {
        lockedControls = Array.from(els.fields.querySelectorAll('input, select, textarea, button'));
        if (els.back) lockedControls.push(els.back);
        lockedControls = lockedControls.filter(function (el) { return !el.disabled; });
        lockedControls.forEach(function (el) { el.disabled = true; });
      } else if (!busy) {
        lockedControls.forEach(function (el) { el.disabled = false; });
        lockedControls = [];
      }
      if (busy) {
        if (!addButtonIdleLabel) addButtonIdleLabel = els.add.textContent || 'ADD TO CART';
        els.add.classList.add('is-busy', 'is-disabled');
        els.add.setAttribute('aria-disabled', 'true');
        els.add.setAttribute('aria-busy', 'true');
        els.add.disabled = true;
        if (label) els.add.textContent = label;
        return;
      }
      els.add.classList.remove('is-busy');
      els.add.removeAttribute('aria-busy');
      els.add.disabled = false;
      if (addButtonIdleLabel) els.add.textContent = addButtonIdleLabel;
    }

    function ensurePersonalizationId() {
      if (!personalizationId) personalizationId = generateCustomizationId();
      return personalizationId;
    }

    function invalidateStepPreviewCache(index) {
      delete stepPreviewCache[String(index)];
    }

    function clearPreviewCache() {
      stepPreviewCache = {};
    }

    function scaleOverlays() {
      if (!els.canvas || !els.zones) return false;
      // Never fall back to CANVAS_REF when the modal is still hidden / unlaid —
      // that paints text at full 720px and looks shifted right until a later resync.
      var w = els.canvas.getBoundingClientRect().width || els.canvas.clientWidth || 0;
      if (w < 2) {
        els.zones.classList.remove('is-scaled');
        return false;
      }
      var scale = w / CANVAS_REF;
      els.zones.style.width = CANVAS_REF + 'px';
      els.zones.style.height = CANVAS_REF + 'px';
      els.zones.style.left = '0';
      els.zones.style.top = '0';
      els.zones.style.transformOrigin = '0 0';
      els.zones.style.transform = 'scale(' + scale + ')';
      els.zones.classList.add('is-scaled');
      return true;
    }

    var overlayLayoutFrame = 0;

    function cancelOverlayLayout() {
      if (overlayLayoutFrame) cancelAnimationFrame(overlayLayoutFrame);
      overlayLayoutFrame = 0;
    }

    function flushOverlayLayout() {
      cancelOverlayLayout();
      if (!root.isConnected || !modal.classList.contains('is-open') ||
          modal._hgScheduleLayout !== scheduleOverlayLayout) return;
      if (scaleOverlays()) fitAllZoneText(els.zones);
    }

    function scheduleOverlayLayout() {
      if (overlayLayoutFrame || !modal.classList.contains('is-open')) return;
      overlayLayoutFrame = requestAnimationFrame(flushOverlayLayout);
    }

    function clearZoom() {
      if (els.canvas) els.canvas.classList.remove('is-zoomed');
      if (els.zoom) {
        els.zoom.style.transform = '';
        els.zoom.style.transformOrigin = '';
      }
      if (els.zones) {
        els.zones.querySelectorAll('.hg-pdp-modal__zone.is-focused').forEach(function (z) {
          z.classList.remove('is-focused');
        });
      }
    }

    function zoomToZone(zoneId) {
      clearZoom();
      if (!zoneId || !els.zoom) return;
      var z =
        formFields().find(function (item) {
          return item.formId === zoneId || item.id === zoneId || overlayIdOf(item) === zoneId;
        }) ||
        sortedZones().find(function (item) {
          return item.id === zoneId || overlayIdOf(item) === zoneId;
        });
      if (!z) return;
      var ox = Number(z.x) + Number(z.w) / 2;
      var oy = Number(z.y) + Number(z.h) / 2;
      var span = Math.max(Number(z.w) || 8, Number(z.h) || 8);
      var scale = Math.min(2.8, Math.max(1.7, 48 / span));
      els.zoom.style.transformOrigin = ox + '% ' + oy + '%';
      els.zoom.style.transform = 'scale(' + scale + ')';
      if (els.canvas) els.canvas.classList.add('is-zoomed');
      var overlayId = (els.zoom && z && overlayIdOf(z)) || zoneId;
      var zoneEl = els.zones && els.zones.querySelector('[data-hg-zone-id="' + overlayId + '"]');
      if (zoneEl) zoneEl.classList.add('is-focused');
    }

    function zoneInlineStyle(z) {
      var align = z.align || 'center';
      var justify = align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center';
      var opacity = z.opacity == null ? 100 : Number(z.opacity);
      return (
        'left:' +
        (Number(z.x) || 0) +
        '%;top:' +
        (Number(z.y) || 0) +
        '%;width:' +
        (Number(z.w) || 0) +
        '%;height:' +
        (Number(z.h) || 0) +
        '%;transform:rotate(' +
        (Number(z.rotation) || 0) +
        'deg);opacity:' +
        opacity / 100 +
        ';' +
        'font-family:' +
        cssFontFamilyValue(z.fontFamily) +
        ';' +
        zoneFillStyleCss(z) +
        'font-size:' +
        effectiveTextFontSize(z) +
        'px;' +
        (z.bold ? 'font-weight:700;' : 'font-weight:400;') +
        (z.italic ? 'font-style:italic;' : 'font-style:normal;') +
        'text-align:' +
        escapeCss(align) +
        ';justify-content:' +
        justify +
        ';letter-spacing:' +
        (Number(z.letterSpacing) || 0) +
        'px;' +
        (Number(z.strokeWidth) > 0
          ? '-webkit-text-stroke:' +
            Number(z.strokeWidth) +
            'px ' +
            escapeCss(z.strokeColor || '#ffffff') +
            ';paint-order:stroke fill;'
          : '') +
        (Number(z.shadowBlur) > 0
          ? 'text-shadow:' +
            (Number(z.shadowOffsetX) || 0) +
            'px ' +
            (Number(z.shadowOffsetY) || 0) +
            'px ' +
            Number(z.shadowBlur) +
            'px ' +
            escapeCss(z.shadowColor || 'rgba(0,0,0,0.45)') +
            ';'
          : '')
      );
    }

    function updatePreview() {
      ensureDesignFonts();
      var groups = overlayGroups();
      var canReuse =
        els.zones.childElementCount === groups.length &&
        groups.every(function (group, i) {
          var node = els.zones.children[i];
          return node && node.getAttribute('data-hg-zone-id') === overlayIdOf(group.zone);
        });

      if (canReuse) {
        groups.forEach(function (group, i) {
          var node = els.zones.children[i];
          if (node) {
            node.setAttribute('style', zoneInlineStyle(group.zone));
            node.classList.toggle('is-glitter-fill', !!zoneGlitterFill(group.zone));
            node.classList.toggle('is-fixed-size', !!group.zone.fixedSize || isDateField(group.zone));
            node.classList.toggle('is-placeholder', groupIsShowingPlaceholder(group));
          }
          var textEl = node && node.querySelector('.hg-pdp-modal__zone-text');
          if (textEl) {
            textEl.textContent = groupDisplayText(group);
            if (groupIsShowingPlaceholder(group) || !(group.zone.fixedSize || isDateField(group.zone))) {
              textEl.style.fontSize = '';
              textEl.style.whiteSpace = '';
              node._hgFitKey = null;
            }
          }
        });
        scheduleOverlayLayout();
        return;
      }

      els.zones.innerHTML = groups
        .map(function (group) {
          var z = group.zone;
          var text = groupDisplayText(group);
          var isPlaceholder = groupIsShowingPlaceholder(group);
          return (
            '<div class="hg-pdp-modal__zone' +
            (isDateField(z) ? ' hg-pdp-modal__zone--date' : ' hg-pdp-modal__zone--text') +
            // Formatted dates can outgrow their design-time text; fit their existing box.
            (z.fixedSize || isDateField(z) ? ' is-fixed-size' : '') +
            (isPlaceholder ? ' is-placeholder' : '') +
            (zoneGlitterFill(z) ? ' is-glitter-fill' : '') +
            '" data-hg-zone-id="' +
            escapeHtml(overlayIdOf(z)) +
            '" style="' +
            zoneInlineStyle(z) +
            '"><span class="hg-pdp-modal__zone-text">' +
            escapeHtml(text) +
            '</span></div>'
          );
        })
        .join('');
      if (modal._hgCanvasResizeObs) {
        modal._hgCanvasResizeObs.disconnect();
        modal._hgCanvasResizeObs.observe(els.canvas);
        Array.from(els.zones.children).forEach(function (zone) {
          modal._hgCanvasResizeObs.observe(zone);
        });
      }
      // Hide until we have a real measured scale (avoids right/out-of-place flash).
      els.zones.classList.remove('is-scaled');
      scheduleOverlayLayout();
    }

    function updateAdd() {
      if (!els.add) return;
      if (els.add.classList.contains('is-busy')) return;
      var fields = formFields();
      var missing = fields.some(function (z) {
        if (!z.required) return false;
        var v = values[z.formId || z.id] || {};
        if (isDateField(z)) return !v.date;
        return !(v.text && String(v.text).trim());
      });
      var overLimit = fields.some(function (z) {
        if (isDateField(z)) return false;
        var max = fieldMaxLength(z);
        if (!max) return false;
        var v = values[z.formId || z.id] || {};
        return String(v.text || '').length > max;
      });
      // Optional-only / empty steps must still allow Next / ADD TO CART.
      var blocked = missing || overLimit;
      els.add.disabled = false;
      els.add.setAttribute('aria-disabled', blocked ? 'true' : 'false');
      els.add.classList.toggle('is-disabled', blocked);
      els.add.style.opacity = blocked ? '0.45' : '1';
    }

    function fieldNotesMarkup(z) {
      var notes = String((z && z.notes) || '').trim();
      if (!notes) return { labelExtra: '', below: '' };
      var asTooltip = String((z && z.notesPosition) || 'below') === 'tooltip';
      if (asTooltip) {
        return {
          labelExtra:
            '<span class="hg-pdp-modal__notes-tip" tabindex="0" role="note" aria-label="' +
            escapeHtml(notes) +
            '">' +
            '<span class="hg-pdp-modal__notes-tip-icon" aria-hidden="true">?</span>' +
            '<span class="hg-pdp-modal__notes-tip-bubble">' +
            escapeHtml(notes) +
            '</span>' +
            '</span>',
          below: '',
        };
      }
      return {
        labelExtra: '',
        below: '<p class="hg-pdp-modal__notes">' + escapeHtml(notes) + '</p>',
      };
    }

    function fieldLabel(z) {
      var prompt = String((z && z.label) || '').trim();
      // Always prefer the Feature Set field label when it looks like a real prompt.
      if (prompt && !isGenericZoneLabel(prompt) && !isShortCanvasPlaceholder(prompt, '')) {
        if (/^enter\b/i.test(prompt) || prompt.length > 3) return prompt;
      }
      var base = zoneDisplayName(z);
      if (isDateField(z)) {
        if (base && /^enter\b/i.test(base)) return base;
        if (base && !isDateCanvasToken(base) && !/^date$/i.test(base) && !isGenericZoneLabel(base)) {
          return base;
        }
        return 'Enter the date';
      }
      if (/groom/i.test(base)) return "Enter Groom's name here";
      if (/bride/i.test(base)) return "Enter Bride's name here";
      if (base && !isGenericZoneLabel(base) && !isShortCanvasPlaceholder(base, prompt) && base !== 'Text') {
        return base;
      }
      if (prompt && !isGenericZoneLabel(prompt)) return prompt;
      if (base && !isShortCanvasPlaceholder(base, '')) return base;
      return 'Enter your text here';
    }

    function fieldErrorMessage(label) {
      return label.replace(/^\*\s*/, '') + ' is required.';
    }

    function clearFieldError(fieldEl) {
      if (!fieldEl) return;
      fieldEl.classList.remove('is-invalid');
      var msg = fieldEl.querySelector('.hg-pdp-modal__error');
      if (msg) {
        msg.hidden = true;
        msg.textContent = '';
      }
    }

    function showFieldErrors() {
      formFields().forEach(function (z) {
        if (!z.required) return;
        var v = values[z.formId || z.id] || {};
        var empty = isDateField(z) ? !v.date : !(v.text && String(v.text).trim());
        var input = els.fields.querySelector(
          '[data-zone-text="' + (z.formId || z.id) + '"], [data-zone-date="' + (z.formId || z.id) + '"]'
        );
        var fieldEl = input && input.closest('.hg-pdp-modal__field');
        if (!fieldEl) return;
        if (!empty) {
          clearFieldError(fieldEl);
          return;
        }
        fieldEl.classList.add('is-invalid');
        var msg = fieldEl.querySelector('.hg-pdp-modal__error');
        if (msg) {
          msg.hidden = false;
          msg.textContent = fieldErrorMessage(fieldLabel(z));
        }
      });
    }

    function applyImageLayer() {
      if (!els.imageLayer) return;
      var layer = (activeStep() && activeStep().imageLayer) || (config && config.imageLayer) || {};
      var x = Number(layer.x) || 0;
      var y = Number(layer.y) || 0;
      var w = Number(layer.w) || 100;
      var h = Number(layer.h) || 100;
      var rot = Number(layer.rotation) || 0;
      var opacity = layer.opacity == null ? 100 : Number(layer.opacity);
      els.imageLayer.style.cssText =
        'left:' +
        x +
        '%;top:' +
        y +
        '%;width:' +
        w +
        '%;height:' +
        h +
        '%;transform:rotate(' +
        rot +
        'deg);opacity:' +
        opacity / 100 +
        ';';
    }

    function renderStepFields() {
      els.fields.innerHTML = formFields()
        .map(function (z) {
          var label = fieldLabel(z);
          var req = z.required ? '<span class="hg-pdp-modal__req">*</span> ' : '';
          var fid = z.formId || z.id;
          var v = values[fid] || {};
          var notes = fieldNotesMarkup(z);
          if (isDateField(z)) {
            return (
              '<div class="hg-pdp-modal__field">' +
              '<label for="hg-zone-' +
              escapeHtml(fid) +
              '">' +
              req +
              escapeHtml(label) +
              notes.labelExtra +
              '</label>' +
              '<div class="hg-pdp-modal__control">' +
              '<div class="hg-pdp-modal__date">' +
              '<span class="hg-pdp-modal__date-icon" aria-hidden="true">' +
              '<svg viewBox="0 0 24 24" width="16" height="16" fill="none"><rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" stroke-width="1.4"/><path d="M4 9h16M8 3v4M16 3v4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>' +
              '</span>' +
              '<input id="hg-zone-' +
              escapeHtml(fid) +
              '" type="date" class="hg-pdp-modal__date-input" data-zone-date="' +
              escapeHtml(fid) +
              '" data-overlay-id="' +
              escapeHtml(overlayIdOf(z)) +
              '" value="' +
              escapeHtml(v.date || '') +
              '"' +
              (z.required ? ' required' : '') +
              '>' +
              '</div>' +
              '<span class="hg-pdp-modal__error-icon" aria-hidden="true">!</span>' +
              '</div>' +
              notes.below +
              '<p class="hg-pdp-modal__error" hidden></p>' +
              '</div>'
            );
          }
          var textVal = v.text || '';
          return (
            '<div class="hg-pdp-modal__field' +
            (fieldMaxLength(z) ? '" data-max-length="' + fieldMaxLength(z) : '') +
            '">' +
            '<label for="hg-zone-' +
            escapeHtml(fid) +
            '">' +
            req +
            escapeHtml(label) +
            notes.labelExtra +
            '</label>' +
            '<div class="hg-pdp-modal__control">' +
            '<input id="hg-zone-' +
            escapeHtml(fid) +
            '" type="text" class="hg-pdp-modal__text-input" data-zone-text="' +
            escapeHtml(fid) +
            '" data-overlay-id="' +
            escapeHtml(overlayIdOf(z)) +
            '" placeholder="" value="' +
            escapeHtml(textVal) +
            '">' +
            '<span class="hg-pdp-modal__error-icon" aria-hidden="true">!</span>' +
            '</div>' +
            notes.below +
            (fieldMaxLength(z)
              ? '<p class="hg-pdp-modal__limit" data-char-limit>' +
                String(textVal).length +
                '/' +
                fieldMaxLength(z) +
                '</p><p class="hg-pdp-modal__limit-warn" data-char-warn' +
                (String(textVal).length > fieldMaxLength(z) ? '' : ' hidden') +
                '>Please stay within ' +
                fieldMaxLength(z) +
                ' characters</p>'
              : '') +
            '<p class="hg-pdp-modal__error" hidden></p>' +
            '</div>'
          );
        })
        .join('');
    }

    function showStep() {
      ensureNextButton();
      syncPrimaryCta();
      applyImageLayer();
      els.image.src = resolveBackgroundImageUrl(root, {
        image: (activeStep() && activeStep().image) || config.image,
      });
      els.image.alt = root.getAttribute('data-product-title') || '';
      setStatus('');
      ensureDesignFonts();
      // Force zone rebuild when switching steps (different overlays).
      if (els.zones) {
        els.zones.innerHTML = '';
        els.zones.classList.remove('is-scaled');
      }
      renderStepFields();
      updatePreview();
      updateAdd();
      clearZoom();
      scheduleOverlayLayout();
    }

    function currentStepValid() {
      var fields = formFields();
      var missing = fields.some(function (z) {
        if (!z.required) return false;
        var v = values[z.formId || z.id] || {};
        if (isDateField(z)) return !v.date;
        return !(v.text && String(v.text).trim());
      });
      var overLimit = fields.some(function (z) {
        if (isDateField(z)) return false;
        var max = fieldMaxLength(z);
        if (!max) return false;
        var v = values[z.formId || z.id] || {};
        return String(v.text || '').length > max;
      });
      return !missing && !overLimit;
    }

    function goNextStep() {
      if (!currentStepValid()) {
        showFieldErrors();
        return;
      }
      // Only add to cart on the real last step — never trust a stale "Next" label.
      if (steps.length < 2 || isLastStep()) {
        addToCart();
        return;
      }
      var leavingIndex = stepIndex;
      var form = findProductForm(root);
      var variantId = getVariantId(root, form);
      var sku = getVariantSku(root, form, variantId, config);
      if (variantId) {
        // Bake completed steps in the background so final ADD TO CART feels instant.
        warmStepPreview(leavingIndex, variantId, sku);
      }
      stepIndex += 1;
      if (stepIndex >= steps.length) {
        stepIndex = steps.length - 1;
        addToCart();
        return;
      }
      showStep();
    }

    function goPrevStep() {
      if (operation) return;
      if (stepIndex <= 0) return;
      stepIndex -= 1;
      showStep();
    }

    function open() {
      if (operation) return;
      if (!config || !steps.length) return;
      if (modal.classList.contains('is-open') && modal._hgClose) {
        modal._hgClose();
        if (modal.classList.contains('is-open')) return;
      }
      previewSession++;
      if (modal._hgCancelLayout) modal._hgCancelLayout();
      modal._hgCancelLayout = cancelOverlayLayout;
      modal._hgScheduleLayout = scheduleOverlayLayout;
      modal._hgFontsChanged = function () {
        clearPreviewCache();
        scheduleOverlayLayout();
      };
      values = {};
      stepIndex = 0;
      personalizationId = '';
      clearPreviewCache();
      stopReadyStatus();
      setAddBusy(false);
      addButtonIdleLabel = '';
      // Show the modal first so canvas has a real width before overlay scaling.
      returnFocus = document.activeElement;
      previousOverflow = document.documentElement.style.overflow;
      inertBackground = Array.from(document.body.children).filter(function (el) { return el !== modal && !el.inert; });
      inertBackground.forEach(function (el) { el.inert = true; });
      modal.hidden = false;
      modal.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      showStep();
      scheduleOverlayLayout();

      els.close.focus();
      modal._hgClose = close;
      modal._hgFieldInput = onFieldInput;
      modal._hgFieldFocus = onFieldFocus;
      modal._hgFieldBlur = onFieldBlur;
      modal._hgAdd = addToCart;
      modal._hgPrimary = goNextStep;
      modal._hgBack = goPrevStep;
      modal._hgShowErrors = showFieldErrors;
    }

    function close() {
      if (operation && operation.posting) return;
      operation = null;
      previewSession++;
      clearPreviewCache();
      cancelOverlayLayout();
      stopReadyStatus();
      setAddBusy(false);
      clearZoom();
      modal.classList.remove('is-open');
      document.documentElement.style.overflow = previousOverflow;
      modal.hidden = true;
      inertBackground.forEach(function (el) { el.inert = false; });
      inertBackground = [];
      if (returnFocus && returnFocus.isConnected) returnFocus.focus();
    }

    function onFieldFocus(e) {
      var t = e.target;
      var id = t.getAttribute('data-zone-text') || t.getAttribute('data-zone-date');
      if (!id) return;
      values[id] = values[id] || {};
      values[id].focused = true;
      zoomToZone(id);
      updatePreview();
      if (t.matches('[data-zone-date]')) openDatePicker(t);
    }

    function onFieldBlur(e) {
      var session = previewSession;
      var t = e.target;
      var id = t && t.getAttribute ? t.getAttribute('data-zone-text') || t.getAttribute('data-zone-date') : '';
      if (id && values[id]) values[id].focused = false;
      var next = e.relatedTarget;
      if (next && next.getAttribute && (next.getAttribute('data-zone-text') || next.getAttribute('data-zone-date'))) {
        updatePreview();
        return;
      }
      setTimeout(function () {
        if (session !== previewSession || !root.isConnected || !config ||
            !modal.classList.contains('is-open') || modal._hgFieldBlur !== onFieldBlur) return;
        var active = document.activeElement;
        if (
          active &&
          active.getAttribute &&
          (active.getAttribute('data-zone-text') || active.getAttribute('data-zone-date'))
        ) {
          return;
        }
        updatePreview();
        clearZoom();
      }, 120);
    }

    function updateCharLimitUi(fieldEl, value) {
      if (!fieldEl) return;
      var max = Number(fieldEl.getAttribute('data-max-length')) || 0;
      if (!max) return;
      var len = String(value || '').length;
      var over = len > max;
      fieldEl.classList.toggle('is-over-limit', over);
      var limitEl = fieldEl.querySelector('[data-char-limit]');
      if (limitEl) {
        limitEl.hidden = false;
        limitEl.textContent = len + '/' + max;
      }
      var warn = fieldEl.querySelector('[data-char-warn]');
      if (warn) warn.hidden = !over;
    }

    function onFieldInput(e) {
      if (operation) return;
      var t = e.target;
      var id = t.getAttribute('data-zone-text') || t.getAttribute('data-zone-date');
      if (!id) return;
      values[id] = values[id] || {};
      if (t.hasAttribute('data-zone-text')) values[id].text = t.value;
      if (t.hasAttribute('data-zone-date')) values[id].date = t.value;
      var fieldEl = t.closest('.hg-pdp-modal__field');
      if (t.hasAttribute('data-zone-text')) updateCharLimitUi(fieldEl, t.value);
      var filled = t.hasAttribute('data-zone-date') ? !!t.value : !!(t.value && String(t.value).trim());
      if (filled && fieldEl && !fieldEl.classList.contains('is-over-limit')) clearFieldError(fieldEl);
      invalidateStepPreviewCache(stepIndex);
      updatePreview();
      updateAdd();
    }

    function buildPreviewZonesPayloadFor(zoneList, preferDomFit) {
      if (preferDomFit) flushOverlayLayout();
      var zones = overlayGroupsFor(zoneList)
        .map(function (group) {
          var z = group.zone;
          var text = groupDisplayText(group);
          // Skip empty placeholders for export — keep static design text.
          if (!isEditableZone(z) && !(z.content || z.text)) return null;
          if (isEditableZone(z) && !text) return null;
          // Don't bake placeholder labels like "Name"/"Date" into the cart preview.
          if (isEditableZone(z)) {
            var parts = group.members.map(function (m) {
              var v = values[m.formId || m.id] || {};
              if (isDateField(m)) {
                return v.date
                  ? formatDateDisplay(v.date, m.dateFormat || 'long', m.datePart || 'full')
                  : '';
              }
              return String(v.text || '').trim();
            });
            if (!parts.filter(Boolean).length) return null;
            text = parts.filter(Boolean).join(
              group.members.length > 1 ? String(z.joinWith != null ? z.joinWith : ' ') : ''
            );
          }
          var fittedSize = 0;
          if (preferDomFit && els.zones) {
            var fittedEl = els.zones.querySelector(
              '[data-hg-zone-id="' + overlayIdOf(z) + '"] .hg-pdp-modal__zone-text'
            );
            fittedSize = fittedEl ? parseFloat(fittedEl.style.fontSize) : 0;
          } else if (z.fixedSize || isDateField(z)) {
            // Earlier steps must be refitted with current fonts too. Measure in
            // the same logical 720px canvas without switching the visible step.
            var measure = document.createElement('div');
            measure.className = 'hg-pdp-modal__zones';
            measure.setAttribute('aria-hidden', 'true');
            measure.style.cssText = 'position:fixed;left:-10000px;top:0;width:720px;height:720px;visibility:hidden;pointer-events:none;';
            var zone = document.createElement('div');
            zone.className = 'hg-pdp-modal__zone hg-pdp-modal__zone--text is-fixed-size';
            zone.setAttribute('style', zoneInlineStyle(z));
            var span = document.createElement('span');
            span.className = 'hg-pdp-modal__zone-text';
            span.textContent = text;
            zone.appendChild(span);
            measure.appendChild(zone);
            modal.appendChild(measure);
            try {
              fitZoneText(zone);
              fittedSize = parseFloat(span.style.fontSize);
            } finally {
              measure.remove();
            }
          }
          return {
            label: zoneDisplayName(z),
            x: Number(z.x) || 0,
            y: Number(z.y) || 0,
            w: Number(z.w) || 0,
            h: Number(z.h) || 0,
            fontSize: fittedSize || effectiveTextFontSize(z),
            fontFamily: z.fontFamily || '',
            color: z.color || '#1b2430',
            align: z.align || 'center',
            bold: !!z.bold,
            italic: !!z.italic,
            rotation: Number(z.rotation) || 0,
            fixedSize: true,
            text: text,
          };
        })
        .filter(Boolean);

      if (!zones.length) return '';
      return JSON.stringify(zones);
    }

    function buildPreviewZonesPayload() {
      return buildPreviewZonesPayloadFor(sortedZones(), true);
    }

    function pickCartPreviewSource() {
      // Prefer the current step, then walk backwards for a step with image + filled overlays.
      var order = [];
      order.push(stepIndex);
      for (var i = steps.length - 1; i >= 0; i--) {
        if (i !== stepIndex) order.push(i);
      }
      for (var o = 0; o < order.length; o++) {
        var idx = order[o];
        var step = steps[idx];
        if (!step) continue;
        var image = resolveBackgroundImageUrl(root, { image: step.image || config.image });
        if (!image) continue;
        var payload = buildPreviewZonesPayloadFor(step.zones || [], idx === stepIndex);
        if (payload) {
          return { image: image, zonesPayload: payload, stepIndex: idx };
        }
      }
      // Fallback: any step image even without overlay text.
      for (var j = 0; j < order.length; j++) {
        var s = steps[order[j]];
        var img = resolveBackgroundImageUrl(root, { image: (s && s.image) || config.image });
        if (img) return { image: img, zonesPayload: '[]', stepIndex: order[j] };
      }
      return {
        image: resolveBackgroundImageUrl(root, config),
        zonesPayload: '[]',
        stepIndex: 0,
      };
    }

    function buildPersonalizationFields() {
      return allFormFields()
        .map(function (z) {
          var v = values[z.formId || z.id] || {};
          var label = zoneDisplayName(z);
          var value = isDateField(z)
            ? formatDateDisplay(v.date || '', z.dateFormat || 'long')
            : String(v.text || '').trim();
          if (!value) return null;
          return { key: label, value: value };
        })
        .filter(Boolean);
    }

    function saveCustomizationRecord(customizationId, previewImage, zonesPayload, variantId, sku, previewWebpBase64, metadata) {
      var apiUrl = resolvePersonalizationApiUrl(
        root.getAttribute('data-personalization-api') || window.__hgPersonalizationApi || ''
      );
      if (!apiUrl) return Promise.reject(new Error('Customization API is not configured'));

      var zones = [];
      try {
        zones = JSON.parse(zonesPayload || '[]');
      } catch (e) {
        zones = [];
      }

      var body = {
        action: 'saveCustomization',
        customizationId: customizationId,
        id: customizationId,
        shop: root.getAttribute('data-shop-domain') || '',
        storefrontHost: root.getAttribute('data-storefront-host') || window.location.host,
        previewPath: root.getAttribute('data-preview-page') || '/pages/customization-preview',
        backgroundUrl: previewImage,
        image: previewImage,
        zones: zones,
        fields: metadata.fields,
        productId: root.getAttribute('data-product-id') || '',
        variantId: String(variantId || ''),
        sku: String(sku || metadata.sku || ''),
        featureSetId: metadata.featureSetId,
        generatePreview: true,
        previewWidth: window.HgPreviewExport ? window.HgPreviewExport.EXPORT_SIZE : 1440,
        previewHeight: window.HgPreviewExport ? window.HgPreviewExport.EXPORT_SIZE : 1440,
      };

      if (previewWebpBase64) {
        body.previewWebpBase64 = previewWebpBase64;
      }

      return fetch(apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then(function (res) {
          return res.text().then(function (text) {
            var data = null;
            try {
              data = text ? JSON.parse(text) : {};
            } catch (e) {
              throw new Error('Customization API returned an unexpected response');
            }
            if (!res.ok || !data.ok) {
              throw new Error((data && data.error) || 'Could not save customization preview');
            }
            if (!data.previewUrl || !isWebpPreviewUrl(data.previewUrl)) {
              throw new Error('Preview upload did not return a WebP URL');
            }
            return data;
          });
        })
        .catch(function (err) {
          if (isAmbiguousCartNetworkError(err)) {
            throw new Error(
              'Could not save customization preview. Please check your connection and try again.'
            );
          }
          throw new Error((err && err.message) || 'Could not save customization preview');
        });
    }

    function tryClientPreviewExport(previewImage, zonesPayload) {
      var zones = [];
      try {
        zones = JSON.parse(zonesPayload || '[]');
      } catch (e) {
        zones = [];
      }
      if (!zones.length) return Promise.resolve('');

      function runExport() {
        if (!window.HgPreviewExport || !window.HgPreviewExport.exportPreview) {
          return Promise.resolve('');
        }
        return window.HgPreviewExport.exportPreview({
          backgroundUrl: previewImage,
          zones: zones,
        })
          .then(function (blob) {
            return window.HgPreviewExport.blobToBase64(blob);
          })
          .catch(function () {
            return '';
          });
      }

      if (window.HgPreviewExport && window.HgPreviewExport.exportPreview) {
        return runExport();
      }

      // Script is deferred — wait briefly so we don't fall back to a plain image.
      return new Promise(function (resolve) {
        var started = Date.now();
        var timer = setInterval(function () {
          if (window.HgPreviewExport && window.HgPreviewExport.exportPreview) {
            clearInterval(timer);
            runExport().then(resolve);
          } else if (Date.now() - started > 2500) {
            clearInterval(timer);
            resolve('');
          }
        }, 40);
      });
    }

    function addToCartWithProperties(properties, variantId, quantity, currentOperation) {
      var cleanProperties = sanitizeCartProperties(properties);
      var addUrl = cartAddUrl();

      setAddBusy(true, 'PLEASE WAIT');
      if (!readyStatusTimer) setStatus('Getting your cart ready…');

      function buildFormData(includeSections) {
        var fd = new FormData();
        fd.append('id', String(Number(variantId) || variantId));
        fd.append('quantity', String(quantity));
        Object.keys(cleanProperties).forEach(function (key) {
          fd.append('properties[' + key + ']', cleanProperties[key]);
        });
        if (includeSections) {
          var sections = getCartAddSectionsParam();
          if (sections) {
            fd.append('sections', sections);
            fd.append('sections_url', window.location.pathname);
          }
        }
        return fd;
      }

      function parseCartResponse(res) {
        return res.text().then(function (text) {
          var data = null;
          try {
            data = text ? JSON.parse(text) : {};
          } catch (e) {
            throw new TypeError('Cart returned an unexpected response. Please check your cart before trying again.');
          }
          if (!res.ok || data.status || data.errors) {
            throw new Error(
              (data && (data.description || data.message || data.errors)) || 'Could not add to bag'
            );
          }
          return data;
        });
      }

      function postCartFormData(includeSections) {
        var cleanBody = buildFormData(includeSections);
        return fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
            'X-Clean-Cart': 'true',
          },
          body: cleanBody,
        }).then(parseCartResponse).then(function (data) {
          window['clean-commerce']?.added(data, [{ id: cleanBody.get('id'), quantity: cleanBody.get('quantity') || 1 }], 'legacy-customizer');
          return data;
        });
      }

      function finishCartSuccess(data) {
        operation = null;
        stopReadyStatus();
        setAddBusy(false);
        close();
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'hadiyah-pdp-customizer',
            cartData: data,
          });
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
        return Promise.resolve().then(function () { return openCartUi(data); }).catch(function () {
          // UI failure is not a failed purchase and must never resubmit it.
          window.location.href = cartPageUrl();
        });
      }

      // Read the exact line baseline before the only POST. Never retry an ambiguous write.
      var baselineQty;
      return fetchCartJson()
        .then(function (cart) {
          if (operation !== currentOperation) return null;
          if (root.isConnected === false) {
            operation = null;
            close();
            return null;
          }
          if (currentOperation.previewRevision != null && currentOperation.previewRevision !== fitFontRevision) {
            throw new Error('Customization fonts changed. Please save your previews again. Your item has not been added.');
          }
          baselineQty = countMatchingCartLines(cart, variantId, cleanProperties);
          currentOperation.posting = true;
          return postCartFormData(false).catch(function (err) {
            if (!isAmbiguousCartNetworkError(err)) throw err;
            return reconcileAmbiguousCartAdd(variantId, quantity, cleanProperties, baselineQty).then(function (confirmed) {
              if (confirmed) return confirmed;
              cartUncertain = true;
              throw err;
            });
          });
        })
        .then(function (data) {
          if (operation !== currentOperation) return;
          return finishCartSuccess(data);
        })
        .catch(function (err) {
          if (operation !== currentOperation) return;
          operation = null;
          stopReadyStatus();
          setAddBusy(false);
          setStatus(networkErrorMessage(err, 'Could not add to bag'), true);
          updateAdd();
        });
    }

    function allStepsValid() {
      for (var i = 0; i < steps.length; i++) {
        var fields = formFieldsFromZones(steps[i].zones || []);
        var missing = fields.some(function (z) {
          if (!z.required) return false;
          var v = values[z.formId || z.id] || {};
          if (isDateField(z)) return !v.date;
          return !(v.text && String(v.text).trim());
        });
        var overLimit = fields.some(function (z) {
          if (isDateField(z)) return false;
          var max = fieldMaxLength(z);
          if (!max) return false;
          var v = values[z.formId || z.id] || {};
          return String(v.text || '').length > max;
        });
        if (missing || overLimit) return false;
      }
      return true;
    }

    function addToCart() {
      if (operation) return;
      if (cartUncertain) {
        setStatus('The last cart update could not be confirmed. Check your cart and refresh this page before adding again.', true);
        return;
      }
      if (!currentStepValid()) {
        showFieldErrors();
        return;
      }
      if (!allStepsValid()) {
        setStatus('Please complete all required fields in earlier steps.', true);
        return;
      }

      var form = findProductForm(root);
      var variantId = getVariantId(root, form);
      if (!variantId) {
        setStatus('Select a variant first.', true);
        return;
      }

      var delivery = validateDelivery(form);
      if (!delivery.ok) {
        setStatus(delivery.message, true);
        return;
      }

      var properties = collectFormProperties(form);
      allFormFields().forEach(function (z) {
        var v = values[z.formId || z.id] || {};
        var label = zoneDisplayName(z);
        if (isDateField(z)) {
          if (v.date) properties[label] = formatDateDisplay(v.date, z.dateFormat || 'long');
          return;
        }
        if (v.text && String(v.text).trim()) properties[label] = String(v.text).trim();
      });

      var sku = getVariantSku(root, form, variantId, config);
      var previewJobs = collectStepPreviewJobs();
      if (previewJobs.length !== steps.length) {
        setStatus('Product image is missing. Add a featured image to this product in Shopify.', true);
        return;
      }

      var currentOperation = {};
      operation = currentOperation;
      var quantity = getQuantity(form);
      var recordId = ensurePersonalizationId();
      setAddBusy(true, 'PLEASE WAIT');
      startReadyStatus();

      function finishWithPreviewUrls(previewUrls) {
        var cartProperties = {};
        var urls = previewUrls;

        Object.keys(properties).forEach(function (key) {
          if (
            (key.charAt(0) === '_' && key !== '_minimum_quantity') ||
            /^preview\./i.test(key) ||
            key === 'Customization Preview'
          ) return;
          if (cartProperties[key] == null && properties[key] != null && properties[key] !== '') {
            cartProperties[key] = properties[key];
          }
        });

        cartProperties._customization_id = recordId;
        if (urls.length) {
          cartProperties._customization_preview = urls[0];
          if (urls.length > 1) {
            cartProperties._customization_previews = urls.join('|');
          }
          publishCustomizationSaved({
            customizationId: cartProperties._customization_id,
            previewUrl: urls[0],
            previewUrls: urls,
            sku: sku,
            variantId: variantId,
            version: 1,
          });
          updatePdpSavedPreview(root, urls[0]);
        }

        if (isMyohCustomizerRoot(root)) {
          // The builder may replace its host synchronously in the completion listener.
          operation = null;
          stopReadyStatus();
          setAddBusy(false);
          close();
          document.dispatchEvent(new CustomEvent('myoh:customization-complete', {
            detail: {
              variantId: variantId,
              customizationId: cartProperties._customization_id,
              properties: cartProperties,
            },
          }));
          return;
        }
        return addToCartWithProperties(cartProperties, variantId, quantity, currentOperation);
      }

      return Promise.all(previewJobs.map(function (job) {
        return warmStepPreview(job.stepIndex, variantId, sku);
      }))
        .then(function (urls) {
          if (operation !== currentOperation) return;
          if (root.isConnected === false) {
            operation = null;
            close();
            return;
          }
          if (urls.length !== previewJobs.length || urls.some(function (url) { return !url; }) ||
              !savedPreviewsAreCurrent(previewJobs, urls)) {
            throw new Error('Could not save every customization preview. Please try again. Your item has not been added.');
          }
          currentOperation.previewRevision = fitFontRevision;
          return finishWithPreviewUrls(urls);
        })
        .catch(function (err) {
          if (operation !== currentOperation) return;
          operation = null;
          stopReadyStatus();
          setAddBusy(false);
          setStatus(err.message || 'Could not save customization. Please try again.', true);
          updateAdd();
        });
    }

    function collectStepPreviewJobs() {
      var idBase = ensurePersonalizationId();
      var jobs = [];
      for (var i = 0; i < steps.length; i++) {
        var step = steps[i];
        var image = resolveBackgroundImageUrl(root, { image: (step && step.image) || config.image });
        if (!image) continue;
        // Prefer live fitted type only for the visible step; other steps use stored sizes.
        var zonesPayload = buildPreviewZonesPayloadFor(step.zones || [], i === stepIndex) || '[]';
        jobs.push({
          stepIndex: i,
          title: (step && step.title) || 'Step ' + (i + 1),
          image: image,
          zonesPayload: zonesPayload,
          fingerprint: stepValuesFingerprint(i, image),
          id: idBase + (steps.length > 1 ? '-s' + (i + 1) : ''),
        });
      }
      if (!jobs.length) {
        var fallback = pickCartPreviewSource();
        if (fallback.image) {
          jobs.push({
            stepIndex: 0,
            title: 'Customization',
            image: fallback.image,
            zonesPayload: fallback.zonesPayload || '[]',
            fingerprint: stepValuesFingerprint(0, fallback.image),
            id: idBase,
          });
        }
      }
      return jobs;
    }

    function stepValuesFingerprint(index, image) {
      var step = steps[index] || steps[0] || {};
      var fields = formFieldsFromZones(step.zones || []);
      var parts = fields.map(function (z) {
        var v = values[z.formId || z.id] || {};
        if (isDateField(z)) return (z.formId || z.id) + ':d:' + String(v.date || '');
        return (z.formId || z.id) + ':t:' + String(v.text || '').trim();
      });
      return String(image || '') + '|' + parts.join('|');
    }

    function savedPreviewsAreCurrent(jobs, urls) {
      return jobs.every(function (job, index) {
        var entry = stepPreviewCache[String(job.stepIndex)];
        return entry && entry.url === urls[index] && entry.revision === fitFontRevision && entry.session === previewSession;
      });
    }

    function waitForPreviewFonts() {
      if (!document.fonts || !document.fonts.load) return Promise.resolve();
      var fonts = {};
      steps.forEach(function (step) {
        (step.zones || []).forEach(function (z) {
          var family = cssFontFamilyValue(z.fontFamily);
          if (family) fonts[(z.italic ? 'italic ' : '') + (z.bold ? '700 ' : '400 ') + '16px ' + family] = true;
        });
      });
      return designFontReady.then(function (loaded) {
        if (!loaded) throw new Error('Could not load customization fonts. Please try again.');
        return Promise.all(Object.keys(fonts).map(function (font) {
          return document.fonts.load(font);
        }));
      }).then(function () { return document.fonts.ready; });
    }

    function warmStepPreview(index, variantId, sku) {
      var session = previewSession;
      // Capture sizes only after font metrics settle, including offscreen steps.
      return waitForPreviewFonts().then(function () {
        if (session !== previewSession || !root.isConnected || modal._hgAdd !== addToCart) return '';
        var job = collectStepPreviewJobs().find(function (job) { return job.stepIndex === index; });
        if (!job) return '';
        var metadata = { fields: buildPersonalizationFields(), sku: config.sku || '', featureSetId: config.featureSetId || '' };
        var revision = fitFontRevision;
        var fingerprint = JSON.stringify([job.fingerprint, job.zonesPayload, variantId, sku, metadata, revision]);
        var key = String(index);
        var cached = stepPreviewCache[key];
        if (cached && cached.fingerprint === fingerprint) return cached.url || cached.promise;
        var entry = { fingerprint: fingerprint, revision: revision, session: session, url: '', promise: null };
        function isCurrent() {
          return session === previewSession && root.isConnected && modal._hgAdd === addToCart &&
            stepPreviewCache[key] === entry && revision === fitFontRevision;
        }
        stepPreviewCache[key] = entry;
        // Serialize replacement saves for the same record. A superseded response
        // cannot overwrite a newer preview or authorize a purchase.
        entry.promise = (stepSaveTails[job.id] || Promise.resolve()).then(function () {
          if (!isCurrent()) return null;
          return tryClientPreviewExport(job.image, job.zonesPayload).then(function (previewWebpBase64) {
            if (!isCurrent()) return null;
            return saveCustomizationRecord(job.id, job.image, job.zonesPayload,
              variantId, sku, previewWebpBase64 || '', metadata);
          });
        }).then(function (saved) {
          if (!isCurrent()) return '';
          entry.url = (saved && saved.previewUrl) || '';
          return entry.url;
        }).catch(function (err) {
          console.warn('[hg-pdp] step preview failed', index, err);
          if (stepPreviewCache[key] === entry) delete stepPreviewCache[key];
          return '';
        });
        stepSaveTails[job.id] = entry.promise;
        entry.promise.then(function () {
          if (stepSaveTails[job.id] === entry.promise) delete stepSaveTails[job.id];
        });
        return entry.promise;
      }).catch(function (err) {
        console.warn('[hg-pdp] preview font preparation failed', err);
        return '';
      });
    }

    bindVariantListener();
    applyVariantSelection(
      getVariantId(root, findProductForm(root)),
      getVariantSku(root, findProductForm(root), getVariantId(root, findProductForm(root)), null)
    );

    var btn =
      root.querySelector('[data-hg-open-customizer]') ||
      root.querySelector('[data-hg-open-customize]');
    var opening = false;
    function gatedOpen(e) {
      if (opening || operation || !config || !steps.length) return;
      if (window.HadiyahDeliveryGate && typeof window.HadiyahDeliveryGate.allow === 'function') {
        if (!window.HadiyahDeliveryGate.allow(e)) return;
      }
      if (e) e.preventDefault();
      opening = true;
      if (btn) {
        btn.classList.add('is-loading');
        btn.setAttribute('aria-busy', 'true');
        btn.setAttribute('aria-disabled', 'true');
      }
      // Allow the click feedback to paint before building the editor.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          try {
            if (root.isConnected) open();
          } finally {
            opening = false;
            if (btn) {
              btn.classList.remove('is-loading');
              btn.removeAttribute('aria-busy');
              btn.removeAttribute('aria-disabled');
            }
          }
        });
      });
    }

    if (btn) {
      btn.addEventListener('click', gatedOpen);
    } else {
      // Fallback: whole block clickable if attribute mismatch in older theme copies
      root.addEventListener('click', function (e) {
        if (e.target.closest('button, a, input, label')) return;
        gatedOpen(e);
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-hg-pdp-customizer]').forEach(createController);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
